/*!

=========================================================
* Argon Dashboard React - v1.2.3
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
// reactstrap components
import Header from 'components/Headers/Header';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardHeader, Container, Row, Table } from 'reactstrap';
import { GlobalContext } from '../api/GlobalContext';

// Additional imports
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Select from 'react-select';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  Button,
  Form,
  Label,
  Nav,
  NavItem,
  NavLink,
  Pagination,
  PaginationItem,
  PaginationLink,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Badge,
  Input,
  Popover,
  PopoverBody,
  TabContent,
  TabPane,
} from 'reactstrap';
import ScoreAllNotesPanel from 'components/Analytics/ScoreAllNotesPanel';
import ScoreAllRunsList from 'components/Analytics/ScoreAllRunsList';
import { getAuthorshipLabels } from 'components/Misc/complianceUtils';
import { gradeForScore, scoreBadgeColor } from 'components/Misc/complianceGrades';

// 'YYYY-MM-DD' as a LOCAL date. new Date('2026-09-25') parses as UTC midnight,
// which renders as the 24th anywhere west of Greenwich.
const parseFilterDate = (value) => {
  if (typeof value !== 'string') return null;
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

// A stored 'YYYY-MM-DD' back into a picker date that serializes to the same
// UTC calendar day, which is all the backend reads (parse_score_all_date), so
// View then Generate asks for exactly the run's range. Same rule as
// daysFromNow: local midnight west of UTC, local noon east of it.
const parseRunDate = (value) => {
  const date = parseFilterDate(value);
  if (date && date.getTimezoneOffset() < 0) date.setHours(12, 0, 0, 0);
  return date;
};

// For browsers west of UTC, local midnight keeps rolling-range dates from
// shifting forward when serialized. East of UTC it would shift them backward.
const daysFromNow = (days) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  if (date.getTimezoneOffset() >= 0) date.setHours(0, 0, 0, 0);
  return date;
};
const DATE_PRESETS = [
  { id: 'last7', label: 'Last 7 days', range: () => [daysFromNow(-7), daysFromNow(1)] },
  { id: 'last30', label: 'Last 30 days', range: () => [daysFromNow(-30), daysFromNow(1)] },
  { id: 'last90', label: 'Last 90 days', range: () => [daysFromNow(-90), daysFromNow(1)] },
  {
    id: 'thisMonth',
    label: 'This month',
    range: () => {
      const start = new Date();
      start.setDate(1);
      start.setHours(0, 0, 0, 0);
      return [start, daysFromNow(1)];
    },
  },
  {
    id: 'lastMonth',
    label: 'Last month',
    range: () => {
      const start = new Date();
      start.setDate(1);
      start.setMonth(start.getMonth() - 1);
      start.setHours(0, 0, 0, 0);
      const end = new Date(start.getFullYear(), start.getMonth() + 1, 0, 23, 59, 59);
      return [start, end];
    },
  },
];

const RULE_SET_OPTIONS = [
  { value: '', label: 'All rule sets' },
  { value: 'general', label: 'General' },
  { value: 'medicare', label: 'Medicare' },
  { value: 'work_comp', label: 'Work Comp' },
  { value: 'peds', label: 'Peds' },
];

// The shared grade scale (components/Misc/complianceGrades.js), re-exported
// for this view's tests.
export { gradeForScore, scoreBadgeColor };

// A clinician with no visits and no graded notes in the period did not
// treat; CORA asked that they not sit in the table and downloads as a 0
// (COM-564). A note signed in the period for an earlier visit still counts.
const clinicianHasVisits = (details) =>
  (Number(details?.total_conversations) || 0) > 0 ||
  (Number(details?.visits_with_score) || 0) > 0;

const NOTE_SOURCE_LABELS = {
  all: 'All notes',
  comprehend: 'Comprehend-graded only',
  non_comprehend: 'Clinician-authored only',
};

// Clinician groups are the clinic's own labels: "PREFIX-Value" (COMPANY-FPT,
// REGION-East, ...). Which prefixes exist is up to each clinic; a label
// without one is in "Other".
export const labelPrefix = (label) => {
  const text = String(label || '');
  const dash = text.indexOf('-');
  return dash > 0 ? text.slice(0, dash).trim() : 'Other';
};
export const labelValue = (label) => {
  const text = String(label || '');
  const dash = text.indexOf('-');
  return dash > 0 ? text.slice(dash + 1).trim() : text;
};

// A clinician is in a report's group filter when, for every group (prefix)
// filtered on, they carry at least one of its selected labels: COMPANY-FPT
// plus REGION-East is FPT clinicians in the East, not either.
export const matchesGroupFilter = (clinicianLabels, selectedLabels) => {
  if (!selectedLabels || selectedLabels.length === 0) return true;
  const have = new Set(clinicianLabels || []);
  const byPrefix = {};
  selectedLabels.forEach((label) => {
    (byPrefix[labelPrefix(label)] = byPrefix[labelPrefix(label)] || []).push(label);
  });
  return Object.values(byPrefix).every((labels) => labels.some((label) => have.has(label)));
};

// A stored report's per-clinician note-type rows as the note-type rows the
// dashboard aggregates. Rows are compact: every rule kept, each as
// [index into `rules`, fully, partially, not met, not applicable].
export const noteTypeRowsFromReport = (rows, rules) =>
  (rows || []).map((row) => ({
    ...row,
    clinician_count: 1,
    has_complete_rule_results_data: (Number(row.missing_rule_evaluation_visits) || 0) === 0,
    rule_results: (row.rule_results || []).map(
      ([ruleIndex, fully, partially, notMet, notApplicable]) => ({
        category: rules?.[ruleIndex]?.[0] || '',
        requirement_text: rules?.[ruleIndex]?.[1] || '',
        fully_met_count: fully,
        partially_met_count: partially,
        not_met_count: notMet,
        not_applicable_count: notApplicable,
      }),
    ),
  }));

// A stored run report (COM-564) holds every clinician at once. The dashboard
// renders query pages, so split it into pages of `pageSize` clinicians. The
// totals and note types ride on page 1 only: the summary and the note-type
// table add pages up, and the report's figures are already whole-query ones.
export const buildReportPages = (report, pageSize) => {
  const clinicians = Object.entries(report?.per_clinician_data || {});
  const totalUsers = clinicians.length;
  const totalPages = Math.max(1, Math.ceil(totalUsers / pageSize));
  const { per_clinician_data: _clinicians, total_data: totalData,
    note_type_data: noteTypeData, pagination: _pagination,
    per_clinician_note_types: _perClinicianNoteTypes, ...rest } = report || {};
  const pages = [];
  for (let page = 1; page <= totalPages; page += 1) {
    const slice = clinicians.slice((page - 1) * pageSize, page * pageSize);
    pages.push({
      ...rest,
      per_clinician_data: Object.fromEntries(slice),
      total_data: page === 1 ? totalData : undefined,
      note_type_data: page === 1 ? noteTypeData || [] : [],
      pagination: {
        current_page: page,
        total_pages: totalPages,
        page_size: pageSize,
        total_users: totalUsers,
        users_in_current_page: slice.length,
        has_next_page: page < totalPages,
        has_previous_page: page > 1,
        start_index: totalUsers > 0 ? (page - 1) * pageSize + 1 : 0,
        end_index: (page - 1) * pageSize + slice.length,
      },
    });
  }
  return pages;
};

// Reports built before "graded X of Y" (COM-564) have no gradeable count, and
// count by the earlier rules; they need a rebuild, not a column of blanks.
export const reportHasGradeableCounts = (report) => {
  const clinicians = Object.values(report?.per_clinician_data || {});
  return clinicians.length === 0 ||
    clinicians.some((details) => details && details.gradeable_notes !== undefined);
};

// One CSV cell: quoted, and never run as a spreadsheet formula.
const csvCell = (value) => {
  let text = value === null || value === undefined ? '' : String(value);
  if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`;
  return `"${text.replace(/"/g, '""')}"`;
};

const downloadCsv = (rows, filename) => {
  const csvContent = rows.map((row) => row.map(csvCell).join(',')).join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const localDateStamp = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const filenamePart = (value) =>
  String(value || '').replace(/[^A-Za-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '');

const formatFilterDate = (date) =>
  date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

// "LOCATION" -> "Location": group prefixes are stored upper-case.
const formatGroupPrefix = (prefix) =>
  prefix.charAt(0).toUpperCase() + prefix.slice(1).toLowerCase();

// react-select, matched to the filter form's native inputs.
const clinicianSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 40,
    borderRadius: 6,
    fontSize: '0.875rem',
    borderColor: state.isFocused ? '#5e72e4' : '#d8dce5',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(94, 114, 228, 0.14)' : 'none',
    '&:hover': { borderColor: state.isFocused ? '#5e72e4' : '#b9c1d0' },
  }),
  placeholder: (base) => ({ ...base, color: '#8898aa' }),
  multiValue: (base) => ({ ...base, background: '#eef1ff', borderRadius: 4 }),
  multiValueLabel: (base) => ({ ...base, color: '#3c4bb8', fontSize: '0.8rem' }),
  menu: (base) => ({ ...base, zIndex: 20 }),
};

// New Component for getting user details
// Exported for AnalyticsDashboard.test.js — apiClient is injected, so the
// summary's load/complete behaviour is testable without the whole dashboard.
export const GetAnalytics = ({ apiClient, onDataLoaded }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [deepLinkJobId] = useState(() => new URLSearchParams(location.search).get('jobId'));

  // Consume the email link so a later tab visit does not download again.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!params.has('jobId')) return;
    params.delete('jobId');
    navigate({
      pathname: location.pathname,
      search: params.toString() ? `?${params.toString()}` : '',
      hash: location.hash,
    }, { replace: true });
  }, [location.pathname, location.search, location.hash, navigate]);

  // Default to the same calendar days as the last-30-days preset.
  const [t1, setT1] = useState(() => daysFromNow(1));
  const [t2, setT2] = useState(() => daysFromNow(-30));
  const [datePreset, setDatePreset] = useState('last30');
  const applyDatePreset = (presetId) => {
    const preset = DATE_PRESETS.find((option) => option.id === presetId);
    const [start, end] = preset.range();
    setT2(start);
    setT1(end);
    setDatePreset(presetId);
  };

  // Pagination state
  const [pageSize] = useState(200);
  const [pagination, setPagination] = useState(null);

  // Cache for storing fetched data
  const [dataCache, setDataCache] = useState({});
  const [rsp, setRsp] = useState({ Status: 'Response will show here.' });
  const [isLoading, setIsLoading] = useState(false);
  const [hasData, setHasData] = useState(false);

  // Sorting state
  const [sortConfig, setSortConfig] = useState(null);
  const [isSortingAcrossCache, setIsSortingAcrossCache] = useState(false);

  // Visit-detail CSV export state
  const [isExportingVisitDetail, setIsExportingVisitDetail] = useState(false);

  // Auto-pagination state
  const [isAutoLoading, setIsAutoLoading] = useState(false);
  const [autoLoadProgress, setAutoLoadProgress] = useState({
    current: 0,
    total: 0,
  });
  const [autoLoadError, setAutoLoadError] = useState(null);

  // Note source filter (COM-390 "Score All Notes"): which notes to include
  // based on how they were authored.
  const [noteSource, setNoteSource] = useState('all');

  // Score-all-notes state for the recent-runs panel's "View" action.
  const [scoreAllRunError, setScoreAllRunError] = useState('');
  const [loadingScoreAllRun, setLoadingScoreAllRun] = useState(false);
  const [downloadJobId, setDownloadJobId] = useState(null);
  const [downloadStatus, setDownloadStatus] = useState('new');
  // Clinicians with no visits in the period are left out of the table, the
  // clinician count and the downloads unless asked for (COM-564).
  const [showNoVisitClinicians, setShowNoVisitClinicians] = useState(false);
  // Labels a stored report is narrowed to after it was built (COM-564): the
  // whole-org report, cut by company/region/... without another run.
  const [reportGroupFilter, setReportGroupFilter] = useState([]);
  // The stored run report on screen (COM-564), or null for a live query.
  const [reportInfo, setReportInfo] = useState(null);
  // The range the per-clinician cards, visit lists and feedback query. For a
  // stored report it is the report's own: whole UTC days, end date included.
  // The pickers' local times would drop the last day (and shift the first),
  // so the cards disagreed with the row they open from (COM-564).
  const queryT1 = reportInfo?.filters?.end_date
    ? `${reportInfo.filters.end_date}T23:59:59.999Z`
    : t1.toISOString();
  const queryT2 = reportInfo?.filters?.start_date
    ? `${reportInfo.filters.start_date}T00:00:00.000Z`
    : t2.toISOString();
  const scoreAllRef = useRef(null);
  // Score All users get their report from the runner; nothing else calls the
  // Lambda analytics for them.
  const reportMode = !!apiClient?.features?.score_all_notes;
  const [scoreAllRunsKey, setScoreAllRunsKey] = useState(0);

  // Compliance rule set filter (General / Medicare / Work Comp / Peds).
  // '' means no filter -- show every rule set, same as today.
  const [selectedRuleSet, setSelectedRuleSet] = useState('');
  // One option per active guideline document, labelled with its name. The
  // value is the document's rule set, which the backend maps to every note
  // that document grades. Falls back to the fixed list if the clinic's
  // documents can't be read (e.g. mini-admins can't list guidelines).
  const [ruleSetOptions, setRuleSetOptions] = useState(RULE_SET_OPTIONS);

  useEffect(() => {
    if (!apiClient?.getComplianceGuidelines) return undefined;
    let cancelled = false;
    Promise.resolve(apiClient.getComplianceGuidelines())
      .then((response) => {
        if (cancelled || !Array.isArray(response?.guidelines)) return;
        const documentOptions = [];
        response.guidelines
          .filter((guideline) => guideline.status === 'active' && guideline.rule_set)
          .sort((a, b) =>
            String(a.document_name || '').localeCompare(String(b.document_name || '')),
          )
          .forEach((guideline) => {
            if (documentOptions.some((option) => option.value === guideline.rule_set)) {
              return;
            }
            documentOptions.push({
              value: guideline.rule_set,
              label:
                guideline.document_name ||
                RULE_SET_OPTIONS.find((option) => option.value === guideline.rule_set)
                  ?.label ||
                guideline.rule_set,
            });
          });
        const options = [RULE_SET_OPTIONS[0], ...documentOptions];
        setRuleSetOptions(options);
        // A value the backend accepts stays, document or not: a View can
        // restore a run's rule set before this list arrives.
        setSelectedRuleSet((current) =>
          options.some((option) => option.value === current) ||
          RULE_SET_OPTIONS.some((option) => option.value === current)
            ? current
            : '',
        );
      })
      .catch((error) => {
        console.error('Unable to load compliance documents for the rule set filter:', error);
      });
    return () => {
      cancelled = true;
    };
  }, [apiClient]);

  // ONE place builds this key. It previously had noteSource on the write side
  // only, so every read missed and the note-type panel rendered empty.
  const visitTypesCacheKey = useCallback(
    (email) =>
      `visit_types_${email}_${queryT1}_${queryT2}_${selectedRuleSet}_${noteSource}`,
    [queryT1, queryT2, selectedRuleSet, noteSource],
  );

  // Label filter state
  const [selectedLabels, setSelectedLabels] = useState([]);
  const [selectedUserEmails, setSelectedUserEmails] = useState([]);
  // Which label-group (prefix) sections are expanded; collapsed by default
  // (accounts can have many groups, so start compact).
  const [expandedLabelGroups, setExpandedLabelGroups] = useState({});
  const [allUsers, setAllUsers] = useState([]);
  const [loadingClinicians, setLoadingClinicians] = useState(true);
  const [noteTypeSortConfig, setNoteTypeSortConfig] = useState(null);
  const [isNoteTypeSummaryOpen, setIsNoteTypeSummaryOpen] = useState(true);
  const [expandedOrganizationNoteType, setExpandedOrganizationNoteType] =
    useState(null);
  const [analyticsActiveTab, setAnalyticsActiveTab] =
    useState('clinicians');

  // Person card and modal state
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [personCardModalOpen, setPersonCardModalOpen] = useState(false);
  const [noteTypeModalOpen, setNoteTypeModalOpen] = useState(false);
  const [selectedNoteType, setSelectedNoteType] = useState(null);
  const [selectedRuleEvaluation, setSelectedRuleEvaluation] = useState(null);

  // Cache for person card data and visit summaries
  const [personCardCache, setPersonCardCache] = useState({});
  const [visitSummaryCache, setVisitSummaryCache] = useState({});

  // Data for person card
  const [visitTypes, setVisitTypes] = useState([]);
  const [visitsList, setVisitsList] = useState([]);
  const [loadingVisitTypes, setLoadingVisitTypes] = useState(false);
  const [loadingVisitsList, setLoadingVisitsList] = useState(false);
  
  // Tab state for person card modal
  const [personCardActiveTab, setPersonCardActiveTab] = useState('overview');
  
  // Search/filter state for visits
  const [visitSearchTerm, setVisitSearchTerm] = useState('');
  const [visitTypeFilter, setVisitTypeFilter] = useState('all');
  
  // Expanded rows state (using email as key)
  const [expandedRows, setExpandedRows] = useState({});
  const [expandedRowTabs, setExpandedRowTabs] = useState({});
  const [expandedRowSearchTerms, setExpandedRowSearchTerms] = useState({});
  const [expandedRowTypeFilters, setExpandedRowTypeFilters] = useState({});
  const [expandedRowCarouselIndex, setExpandedRowCarouselIndex] = useState({});
  const [expandedRowFeedback, setExpandedRowFeedback] = useState({});
  const expandedRowFeedbackRequests = useRef(new Set());
  const ruleEvaluationPopoverRef = useRef(null);

  // Helper function to format aggregated feedback as bullet points
  const formatAggregatedFeedback = (feedbackText) => {
    if (!feedbackText) return [];
    
    // Remove any existing bullet markers (•, -, *, numbers, etc.)
    let cleanedText = feedbackText
      .replace(/^[•\-\*]\s+/gm, '') // Remove leading bullets
      .replace(/^\d+\.\s+/gm, '') // Remove numbered lists
      .trim();
    
    // If it's already in a list format (newlines), split by newlines
    if (cleanedText.includes('\n')) {
      return cleanedText
        .split('\n')
        .map(s => s.trim().replace(/^[•\-\*]\s*/, '').replace(/^\d+\.\s*/, ''))
        .filter(s => s.length > 0);
    }
    
    // Split by sentence boundaries followed by common feedback starters
    let parts = cleanedText.split(/\.\s+(?=[A-Z][a-z]+ (?:lack|need|missing|frequent|recurring|emphasis|importance|consistent|there|this|documentation|many|the))/i);
    
    // If that didn't work well, try splitting by "There is" patterns
    if (parts.length === 1) {
      parts = cleanedText.split(/\.\s+(?=There is (?:a|an))/i);
    }
    
    // If still one part, try splitting by common conjunctions
    if (parts.length === 1) {
      parts = cleanedText.split(/\.\s+(?=and|or|but)/i);
    }
    
    // Clean up and filter
    const bulletPoints = parts
      .map(s => s.trim().replace(/^\.\s*/, '').replace(/^[•\-\*]\s*/, ''))
      .filter(s => s.length > 10) // Filter out very short fragments
      .map(s => {
        // Ensure it ends with proper punctuation
        if (!s.match(/[.!?]$/)) {
          return s + '.';
        }
        return s;
      });
    
    // If we still have only one long point, try to split by commas in long sentences
    if (bulletPoints.length === 1 && bulletPoints[0].length > 150) {
      const longText = bulletPoints[0];
      // Split by commas that are followed by capital letters (likely new points)
      const commaSplit = longText.split(/,\s+(?=[A-Z][a-z]+ (?:lack|need|missing|frequent|recurring|emphasis|importance|consistent|there|this|documentation|many|the))/i);
      if (commaSplit.length > 1) {
        return commaSplit
          .map(s => s.trim().replace(/^[•\-\*]\s*/, ''))
          .filter(s => s.length > 10)
          .map(s => s.endsWith('.') ? s : s + '.');
      }
    }
    
    return bulletPoints.length > 0 ? bulletPoints : [cleanedText];
  };

  // Authorship tag copy (COM-390 "Score All Notes"). Single source of
  // truth shared with complianceUtils.js so this can't drift.
  const authorshipLabels = getAuthorshipLabels(apiClient);

  const getNoteTypeAverageScore = (noteType) => {
    if ((Number(noteType.visits_with_score) || 0) === 0) return '--';

    const rawAverageScore = noteType.average_score;
    if (
      rawAverageScore === null ||
      rawAverageScore === undefined ||
      (typeof rawAverageScore === 'string' && rawAverageScore.trim() === '')
    ) {
      return '--';
    }

    const averageScore = Number(rawAverageScore);
    return Number.isFinite(averageScore) ? Math.round(averageScore) : '--';
  };

  const getNoteTypeDisplayGrade = (noteType) => {
    const averageScore = getNoteTypeAverageScore(noteType);
    if (averageScore === '--') return '--';
    if (noteType.grade && noteType.grade !== '--') return noteType.grade;
    return gradeForScore(averageScore);
  };

  const getNoteTypeGradeColor = (noteType) => {
    const grade = getNoteTypeDisplayGrade(noteType);
    if (grade === 'A') return 'success';
    if (grade === 'B' || grade === 'C') return 'warning';
    if (grade === '--') return 'secondary';
    return 'danger'; // F
  };

  // "Graded X of Y": Y is every gradeable note (signed, or untouched for 72
  // hours), X the ones graded so far. Responses before it existed lack Y.
  const gradedOf = (graded, gradeable) => {
    const notes = (count) => `${count} ${count === 1 ? 'note' : 'notes'}`;
    if (gradeable === undefined || gradeable === null) return `${notes(graded)} graded`;
    return `${graded} of ${notes(Number(gradeable) || 0)} graded`;
  };

  const getNoteTypeEvaluationSummary = (noteType) =>
    gradedOf(Number(noteType.visits_with_score) || 0, noteType.gradeable_notes);

  const getEvaluatedVisitCount = (details) =>
    details.visits_with_score === null ||
    details.visits_with_score === undefined
      ? null
      : Math.max(0, Number(details.visits_with_score) || 0);

  // Generate cache key for current parameters. Includes noteSource so
  // switching the "All / Comprehend-graded / Clinician-authored" toggle
  // can't accidentally serve cached results fetched under a different
  // note-source filter (COM-390).
  const getCacheKey = useCallback(
    (
      t1,
      t2,
      page,
      pageSize,
      labels = [],
      userEmails = [],
      ruleSet = '',
      source = 'all',
    ) => {
      const labelKey = [...labels].sort().join('|');
      const userEmailKey = [...userEmails].sort().join('|');
      return `${t1.toISOString()}_${t2.toISOString()}_${page}_${pageSize}_${labelKey}_${userEmailKey}_${ruleSet}_${source}`;
    },
    [],
  );

  // Fetch users with labels for filtering
  const fetchUsersWithLabels = useCallback(async () => {
    try {
      const response = await apiClient.getGroupSubscriptionStats({});
      if (response && response.users) {
        const userArray = Object.entries(response.users).map(
          ([email, details]) => ({
            ...details,
            email: email.toLowerCase(),
            labels: details.labels || [],
          }),
        );
        setAllUsers(userArray);
      }
    } catch (error) {
      console.error('Error fetching users with labels:', error);
    } finally {
      setLoadingClinicians(false);
    }
  }, [apiClient]);

  // Get all unique labels from users
  const getAllUniqueLabels = useCallback(() => {
    const labelSet = new Set();
    allUsers.forEach((user) => {
      if (user.labels && Array.isArray(user.labels)) {
        user.labels.forEach((label) => labelSet.add(label));
      }
    });
    return Array.from(labelSet).sort();
  }, [allUsers]);

  // Group labels by prefix (text before the first "-"), e.g., LOCATION-*, DIVISION-*
  // Any group with only one label is merged into an "Other" catch-all bucket.
  const groupLabelsByPrefix = (labels) => {
    const groups = {};
    const catchAllKey = 'Other';
    const catchAll = [];

    labels.forEach((label) => {
      const prefix = (label.split('-')[0] || 'Other').trim() || 'Other';
      if (!groups[prefix]) {
        groups[prefix] = [];
      }
      groups[prefix].push(label);
    });

    // Pull single-item groups into catch-all
    Object.entries(groups).forEach(([key, vals]) => {
      if (vals.length === 1) {
        catchAll.push(vals[0]);
        delete groups[key];
      }
    });

    // Merge any existing "Other" group
    if (groups[catchAllKey]) {
      catchAll.push(...groups[catchAllKey]);
      delete groups[catchAllKey];
    }

    if (catchAll.length > 0) {
      groups[catchAllKey] = catchAll.sort();
    }

    // Sort groups and labels for stable UI
    Object.keys(groups).forEach((key) => groups[key].sort());
    return Object.fromEntries(
      Object.entries(groups).sort(([a], [b]) => a.localeCompare(b)),
    );
  };

  // Filter users by selected labels (OR logic - user must have at least one selected label)
  const filterUsersByLabels = useCallback(
    (userList) => {
      if (selectedLabels.length === 0) {
        return userList; // No filter applied
      }

      return userList.filter((user) => {
        if (!user.labels || user.labels.length === 0) {
          return false; // User has no labels
        }
        // Check if user has at least one of the selected labels
        return user.labels.some((label) => selectedLabels.includes(label));
      });
    },
    [selectedLabels],
  );

  // Auto-load all pages with data passed directly
  const autoLoadAllPagesWithData = useCallback(
    async (firstPageData, forceRefresh = false) => {
      console.log('autoLoadAllPagesWithData called');
      console.log('firstPageData:', firstPageData);

      if (!firstPageData?.pagination) {
        console.log('No pagination data available, returning');
        return;
      }

      const { current_page, total_pages, has_next_page } =
        firstPageData.pagination;
      console.log(
        `Auto-loading: current_page=${current_page}, total_pages=${total_pages}, has_next_page=${has_next_page}`,
      );

      if (!has_next_page) {
        console.log('No more pages to load');
        return;
      }

      setAutoLoadError(null);
      setIsAutoLoading(true);
      setAutoLoadProgress({ current: current_page, total: total_pages });

      // Start from the next page after current
      for (let page = current_page + 1; page <= total_pages; page++) {
        try {
          console.log(`Loading page ${page} of ${total_pages}...`);

          // Wait for the current page to load before moving to next
          await new Promise((resolve, reject) => {
            const cacheKey = getCacheKey(
              t1,
              t2,
              page,
              pageSize,
              selectedLabels,
              selectedUserEmails,
              selectedRuleSet,
              noteSource,
            );
            console.log(`Cache key for page ${page}:`, cacheKey);

            // Check if already cached
            if (!forceRefresh && dataCache[cacheKey]) {
              console.log(`Page ${page} already cached, skipping...`);
              setAutoLoadProgress({ current: page, total: total_pages });
              resolve();
              return;
            }

            console.log(`Making API call for page ${page}...`);
            // Load the page
            apiClient
              .getUserAnalytics(
                t1.toISOString(),
                t2.toISOString(),
                page,
                pageSize,
                selectedLabels,
                selectedUserEmails,
                selectedRuleSet,
                noteSource,
              )
              .then((response) => {
                console.log(`API response for page ${page}:`, response);
                if (response.error) {
                  console.error(`Error loading page ${page}:`, response.error);
                  reject(new Error(response.error));
                  return;
                }

                console.log(`Page ${page} loaded successfully`);

                // Cache the response
                setDataCache((prevCache) => {
                  const newCache = {
                    ...prevCache,
                    [cacheKey]: response,
                  };
                  console.log('Updated cache:', newCache);
                  return newCache;
                });

                setAutoLoadProgress({ current: page, total: total_pages });
                resolve();
              })
              .catch((error) => {
                console.error(`Error loading page ${page}:`, error);
                reject(error);
              });
          });

          // Small delay between pages to avoid overwhelming server
          console.log(`Waiting 300ms before next page...`);
          await new Promise((resolve) => setTimeout(resolve, 300));
        } catch (error) {
          console.error(`Error loading page ${page}:`, error);
          setAutoLoadError({ page, total: total_pages });
          break;
        }
      }

      console.log('Auto-load completed');
      setIsAutoLoading(false);
      setAutoLoadProgress({ current: 0, total: 0 });
    },
    [
      apiClient,
      t1,
      t2,
      pageSize,
      dataCache,
      getCacheKey,
      selectedLabels,
      selectedUserEmails,
      selectedRuleSet,
      noteSource,
    ],
  );

  // Auto-load all pages
  const autoLoadAllPages = useCallback(async () => {
    console.log('autoLoadAllPages called');
    console.log('rsp.data:', rsp.data);
    console.log('rsp.data?.pagination:', rsp.data?.pagination);

    if (!rsp.data?.pagination) {
      console.log('No pagination data available, returning');
      return;
    }

    const { current_page, total_pages, has_next_page } = rsp.data.pagination;
    console.log(
      `Auto-loading: current_page=${current_page}, total_pages=${total_pages}, has_next_page=${has_next_page}`,
    );

    if (!has_next_page) {
      console.log('No more pages to load');
      return;
    }

    setIsAutoLoading(true);
    setAutoLoadProgress({ current: current_page, total: total_pages });

    // Start from the next page after current
    for (let page = current_page + 1; page <= total_pages; page++) {
      try {
        console.log(`Loading page ${page} of ${total_pages}...`);

        // Wait for the current page to load before moving to next
        await new Promise((resolve, reject) => {
          const cacheKey = getCacheKey(
            t1,
            t2,
            page,
            pageSize,
            selectedLabels,
            selectedUserEmails,
            selectedRuleSet,
            noteSource,
          );
          console.log(`Cache key for page ${page}:`, cacheKey);

          // Check if already cached
          if (dataCache[cacheKey]) {
            console.log(`Page ${page} already cached, skipping...`);
            setAutoLoadProgress({ current: page, total: total_pages });
            resolve();
            return;
          }

          console.log(`Making API call for page ${page}...`);
          // Load the page
          apiClient
            .getUserAnalytics(
              t1.toISOString(),
              t2.toISOString(),
              page,
              pageSize,
              selectedLabels,
              selectedUserEmails,
              selectedRuleSet,
              noteSource,
            )
            .then((response) => {
              console.log(`API response for page ${page}:`, response);
              if (response.error) {
                console.error(`Error loading page ${page}:`, response.error);
                reject(new Error(response.error));
                return;
              }

              console.log(`Page ${page} loaded successfully`);

              // Cache the response
              setDataCache((prevCache) => {
                const newCache = {
                  ...prevCache,
                  [cacheKey]: response,
                };
                console.log('Updated cache:', newCache);
                return newCache;
              });

              setAutoLoadProgress({ current: page, total: total_pages });
              resolve();
            })
            .catch((error) => {
              console.error(`Error loading page ${page}:`, error);
              reject(error);
            });
        });

        // Small delay between pages to avoid overwhelming server
        console.log(`Waiting 300ms before next page...`);
        await new Promise((resolve) => setTimeout(resolve, 300));
      } catch (error) {
        console.error(`Error loading page ${page}:`, error);
        break;
      }
    }

    console.log('Auto-load completed');
    setIsAutoLoading(false);
    setAutoLoadProgress({ current: 0, total: 0 });
  }, [
    rsp.data?.pagination,
    apiClient,
    t1,
    t2,
    pageSize,
    dataCache,
    getCacheKey,
    selectedLabels,
    selectedUserEmails,
    selectedRuleSet,
    noteSource,
  ]);

  // The cached pages that belong to the query on screen RIGHT NOW.
  //
  // dataCache is keyed by date range, page, page size and filters, and it
  // outlives all of them — switching a label filter or a page size leaves the
  // old query's pages sitting in it. Everything below used to walk the whole
  // cache, so a second query's totals were the two queries added together and
  // the clinician table showed people who weren't in the current filter at
  // all. Rebuild this query's keys instead of trusting whatever is in there.
  const currentQueryPages = useCallback(() => {
    const totalPages = rsp.data?.pagination?.total_pages || 0;
    const pages = [];
    for (let page = 1; page <= totalPages; page++) {
      const cached = dataCache[
        getCacheKey(
          t1,
          t2,
          page,
          pageSize,
          selectedLabels,
          selectedUserEmails,
          selectedRuleSet,
          noteSource,
        )
      ];
      if (cached) pages.push(cached);
    }
    return pages;
  }, [
    rsp.data?.pagination?.total_pages,
    dataCache,
    getCacheKey,
    t1,
    t2,
    pageSize,
    selectedLabels,
    selectedUserEmails,
    selectedRuleSet,
    noteSource,
  ]);

  // Each clinician's labels, as the clinic has them now.
  const emailLabels = useMemo(
    () => Object.fromEntries(allUsers.map((user) => [user.email, user.labels || []])),
    [allUsers],
  );
  const groupFilterActive = !!reportInfo && reportGroupFilter.length > 0;
  // False only for a stored report built before the gradeable count existed.
  const showGradeable = !reportInfo || reportInfo.hasGradeableCounts !== false;
  const inGroupFilter = (email) =>
    !groupFilterActive ||
    matchesGroupFilter(emailLabels[String(email).toLowerCase()] || [], reportGroupFilter);

  // What the group filter offers: [{prefix, options: [{label, count}]}], only
  // values that would narrow what is on screen to someone. A value is offered
  // when some clinician matching the rest of the filter has it and not all of
  // them do -- so a report already run for one company does not offer that
  // company (or any other, which would leave nobody). Values already picked
  // stay, so they can be removed. Empty for a clinic without labels.
  const reportGroups = useMemo(() => {
    if (!reportInfo) return [];
    const emails = new Set();
    Object.values(dataCache).forEach((cachedPage) => {
      Object.keys(cachedPage?.per_clinician_data || {}).forEach((email) => {
        emails.add(email.toLowerCase());
      });
    });
    const prefixes = new Set();
    emails.forEach((email) => {
      (emailLabels[email] || []).forEach((label) => prefixes.add(labelPrefix(label)));
    });
    const groups = [];
    prefixes.forEach((prefix) => {
      // The rest of the filter: other groups' picks. Picks within this group
      // widen it (either value), so they are left out here.
      const others = reportGroupFilter.filter((label) => labelPrefix(label) !== prefix);
      const base = [...emails].filter((email) =>
        matchesGroupFilter(emailLabels[email] || [], others));
      const counts = {};
      base.forEach((email) => {
        new Set((emailLabels[email] || []).filter((label) => labelPrefix(label) === prefix))
          .forEach((label) => { counts[label] = (counts[label] || 0) + 1; });
      });
      const options = Object.entries(counts)
        .filter(([label, count]) => count < base.length || reportGroupFilter.includes(label))
        .map(([label, count]) => ({ label, count }))
        .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
      if (options.length > 0) {
        groups.push({ prefix, options, reach: base.length });
      }
    });
    return groups.sort((a, b) =>
      b.options.reduce((n, o) => n + o.count, 0) - a.options.reduce((n, o) => n + o.count, 0) ||
      a.prefix.localeCompare(b.prefix));
  }, [reportInfo, dataCache, emailLabels, reportGroupFilter]);

  // Every clinician of the query on screen in the group filter, by email.
  const groupClinicians = () => {
    const clinicians = {};
    currentQueryPages().forEach((cachedData) => {
      Object.entries(cachedData?.per_clinician_data || {}).forEach(([email, details]) => {
        if (inGroupFilter(email)) clinicians[email.toLowerCase()] = details;
      });
    });
    return clinicians;
  };

  // Clinicians in this query with no visits in the period.
  const countNoVisitClinicians = () =>
    Object.values(groupClinicians()).filter((details) => !clinicianHasVisits(details)).length;

  // Get aggregated total data from the cached pages of the current query
  const getAggregatedTotalData = () => {
    if (!rsp.data?.pagination) return null;

    const aggregatedData = {
      totalNumberOfClinicians: 0,
      totalNumberOfConversations: 0,
      totalTimeSaved: 0,
      totalPatientsSeen: 0,
    };

    if (groupFilterActive) {
      // The report's totals are clinic-wide; a group's are its clinicians'
      // rows added up. Patients seen by two of them count twice.
      Object.values(groupClinicians()).forEach((details) => {
        aggregatedData.totalNumberOfClinicians += 1;
        aggregatedData.totalNumberOfConversations += Number(details.total_conversations) || 0;
        aggregatedData.totalTimeSaved += Number(details.total_time_saved_in_hours) || 0;
        aggregatedData.totalPatientsSeen += Number(details.unique_patients_seen) || 0;
      });
    }

    currentQueryPages().forEach((cachedData) => {
      if (!groupFilterActive && cachedData && cachedData.total_data) {
        aggregatedData.totalNumberOfClinicians +=
          cachedData.total_data.totalNumberOfClinicians || 0;
        aggregatedData.totalNumberOfConversations +=
          cachedData.total_data.totalNumberOfConversations || 0;
        aggregatedData.totalTimeSaved +=
          cachedData.total_data.totalTimeSaved || 0;
        aggregatedData.totalPatientsSeen +=
          cachedData.total_data.totalPatientsSeen || 0;
      }
    });

    // The backend counts every active clinician; hidden ones come off.
    aggregatedData.noVisitClinicians = countNoVisitClinicians();
    if (!showNoVisitClinicians) {
      aggregatedData.totalNumberOfClinicians = Math.max(
        0,
        aggregatedData.totalNumberOfClinicians - aggregatedData.noVisitClinicians,
      );
    }

    return aggregatedData;
  };

  /** Pages of the current query that have landed, and how many there are. */
  const loadProgress = () => ({
    loaded: currentQueryPages().length,
    total: rsp.data?.pagination?.total_pages || 0,
  });

  // A group filter on a report built before per-clinician rows existed can
  // narrow the clinicians but not the note types.
  const perClinicianNoteTypes = reportInfo?.perClinicianNoteTypes;
  const noteTypesIgnoreGroupFilter = groupFilterActive && !perClinicianNoteTypes;

  const getAggregatedNoteTypeData = () => {
    const noteTypeData = {};
    const fromClinicianRows = groupFilterActive && perClinicianNoteTypes;
    const sourcePages = fromClinicianRows
      ? [{
        note_type_data: Object.entries(perClinicianNoteTypes.clinicians || {})
          .filter(([email]) => inGroupFilter(email))
          .flatMap(([, rows]) => noteTypeRowsFromReport(rows, perClinicianNoteTypes.rules)),
      }]
      : currentQueryPages();

    sourcePages.forEach((cachedData) => {
      (cachedData?.note_type_data || []).forEach((noteType) => {
        const noteTypeKey =
          noteType.note_type_key ||
          String(noteType.config_name || 'Unknown').trim().toLowerCase();

        if (!noteTypeData[noteTypeKey]) {
          noteTypeData[noteTypeKey] = {
            note_type_key: noteTypeKey,
            config_name: noteType.config_name || 'Unknown',
            clinician_count: 0,
            gradeable_notes: 0,
            total_visits: 0,
            visits_with_score: 0,
            total_score: 0,
            failed_visits: 0,
            rule_evaluation_visits: 0,
            missing_rule_evaluation_visits: 0,
            rule_results_by_key: {},
            has_complete_rule_results_data: true,
            // COM-390 "Score All Notes": authorship breakdown. Backend
            // field names aren't finalized (parallel work) -- only set
            // has_authorship_data when the source actually sends either
            // count, so the summary card can silently show nothing until
            // the backend ships this instead of always showing "0 · 0".
            ai_authored_count: 0,
            clinician_authored_count: 0,
            has_authorship_data: false,
          };
        }

        const aggregate = noteTypeData[noteTypeKey];
        const ruleResults = Array.isArray(noteType.rule_results)
          ? noteType.rule_results
          : [];
        aggregate.has_complete_rule_results_data =
          aggregate.has_complete_rule_results_data &&
          Array.isArray(noteType.rule_results) &&
          noteType.has_complete_rule_results_data !== false;
        aggregate.clinician_count += Number(noteType.clinician_count) || 0;
        aggregate.gradeable_notes += Number(noteType.gradeable_notes) || 0;
        aggregate.total_visits += Number(noteType.total_visits) || 0;
        aggregate.visits_with_score +=
          Number(noteType.visits_with_score) || 0;
        aggregate.total_score += Number(noteType.total_score) || 0;
        aggregate.failed_visits += Number(noteType.failed_visits) || 0;
        if (
          noteType.ai_authored_count !== undefined ||
          noteType.clinician_authored_count !== undefined
        ) {
          aggregate.has_authorship_data = true;
        }
        aggregate.ai_authored_count +=
          Number(noteType.ai_authored_count) || 0;
        aggregate.clinician_authored_count +=
          Number(noteType.clinician_authored_count) || 0;
        aggregate.rule_evaluation_visits +=
          Number(noteType.rule_evaluation_visits) || 0;
        aggregate.missing_rule_evaluation_visits +=
          Number(noteType.missing_rule_evaluation_visits) || 0;
        ruleResults.forEach((rule) => {
          const category = String(rule.category || '').trim();
          const requirementText = String(rule.requirement_text || '').trim();
          if (!requirementText) return;

          const ruleKey = JSON.stringify([
            category.toLowerCase(),
            requirementText.toLowerCase(),
          ]);
          if (!aggregate.rule_results_by_key[ruleKey]) {
            aggregate.rule_results_by_key[ruleKey] = {
              category,
              requirement_text: requirementText,
              fully_met_count: 0,
              partially_met_count: 0,
              not_met_count: 0,
              not_applicable_count: 0,
            };
          }

          const ruleAggregate = aggregate.rule_results_by_key[ruleKey];
          ruleAggregate.not_applicable_count +=
            Number(rule.not_applicable_count) || 0;
          ruleAggregate.fully_met_count +=
            Number(rule.fully_met_count) || 0;
          ruleAggregate.partially_met_count +=
            Number(rule.partially_met_count) || 0;
          ruleAggregate.not_met_count += Number(rule.not_met_count) || 0;
        });
      });
    });

    return Object.values(noteTypeData)
      // A note type with nothing graded or waiting to be graded is left out.
      .filter((noteType) =>
        !fromClinicianRows ||
        noteType.visits_with_score > 0 ||
        noteType.gradeable_notes > 0)
      .map((noteType) => {
        const { rule_results_by_key: ruleResultsByKey, ...summary } = noteType;
        if (!showGradeable) delete summary.gradeable_notes;
        return {
          ...summary,
          average_score:
            noteType.visits_with_score > 0
              ? noteType.total_score / noteType.visits_with_score
              : null,
          rule_results: Object.values(ruleResultsByKey).sort(
            (a, b) =>
              b.not_met_count - a.not_met_count ||
              b.partially_met_count - a.partially_met_count ||
              a.requirement_text.localeCompare(b.requirement_text),
          ),
        };
      })
      .sort(
        (a, b) =>
          b.total_visits - a.total_visits ||
          a.config_name.localeCompare(b.config_name),
      );
  };

  // Convert clinician data to array format for sorting - use all cached data
  const getAllCachedClinicianData = () => {
    if (!rsp.data) return [];

    const allClinicians = [];

    currentQueryPages().forEach((cachedData) => {
      if (cachedData && cachedData.per_clinician_data) {
        Object.entries(cachedData.per_clinician_data).forEach(
          ([email, details]) => {
            // Check if this clinician is already in our array (avoid duplicates)
            const existingIndex = allClinicians.findIndex(
              (c) => c.email === email,
            );
            if (existingIndex === -1) {
              allClinicians.push({
                email,
                visits: details.total_conversations,
                evaluatedVisits: getEvaluatedVisitCount(details),
                gradeableNotes: details.gradeable_notes,
                timeSaved: details.total_time_saved_in_hours,
                activeDay: details.most_active_day || 'N/A',
                visitsOnActiveDay: details['visits on most active day'],
                patientsSeen: details.unique_patients_seen,
                avgComplianceScore: details.average_compliance_score,
                fComplianceVisits: details.num_of_notes_with_f_compliance,
                averageScore: details.average_score,
                ...details, // Keep original details for other fields
              });
            }
          },
        );
      }
    });

    return allClinicians.filter(
      (clinician) =>
        inGroupFilter(clinician.email) &&
        (showNoVisitClinicians || clinicianHasVisits(clinician)),
    );
  };

  // Hiding the clinicians with no visits empties whole pages (the report is
  // busiest first, so they are the last pages); page through the clinicians
  // left instead of the fixed 200-per-page chunks.
  const hidingNoVisitClinicians = !showNoVisitClinicians && countNoVisitClinicians() > 0;
  const pageAcrossCache =
    (isSortingAcrossCache && !!sortConfig) || hidingNoVisitClinicians || groupFilterActive;

  // Get data for display - either current page only or all cached data based on sorting state
  const getDisplayData = () => {
    if (!rsp.data) return [];

    let clinicianData;

    // If user has explicitly sorted, or clinicians are hidden, use all cached
    // data and page through what is left.
    if (pageAcrossCache) {
      clinicianData = getAllCachedClinicianData();
    } else {
      // Otherwise, use only current page data
      clinicianData = Object.entries(rsp.data.per_clinician_data).map(
        ([email, details]) => ({
          email: email.toLowerCase(),
          visits: details.total_conversations,
          evaluatedVisits: getEvaluatedVisitCount(details),
          gradeableNotes: details.gradeable_notes,
          timeSaved: details.total_time_saved_in_hours,
          activeDay: details.most_active_day || 'N/A',
          visitsOnActiveDay: details['visits on most active day'],
          patientsSeen: details.unique_patients_seen,
          avgComplianceScore: details.average_compliance_score,
          fComplianceVisits: details.num_of_notes_with_f_compliance,
          averageScore: details.average_score,
          ...details, // Keep original details for other fields
        }),
      );
    }

    return clinicianData.filter(
      (clinician) =>
        inGroupFilter(clinician.email) &&
        (showNoVisitClinicians || clinicianHasVisits(clinician)),
    );
  };

  const clinicianDataArray = getDisplayData();

  // Custom sorting logic
  const sortedClinicians = useMemo(() => {
    if (!sortConfig || !Array.isArray(clinicianDataArray)) {
      return clinicianDataArray;
    }

    return [...clinicianDataArray].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      // Handle numeric fields
      const numericFields = [
        'visits',
        'timeSaved',
        'visitsOnActiveDay',
        'patientsSeen',
        'avgComplianceScore',
        'fComplianceVisits',
        'averageScore',
      ];
      if (numericFields.includes(sortConfig.key)) {
        const aNum = Number(aValue) || 0;
        const bNum = Number(bValue) || 0;
        return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
      }

      // Handle string fields
      const aStr = String(aValue || '').toLowerCase();
      const bStr = String(bValue || '').toLowerCase();
      return sortConfig.direction === 'asc'
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr);
    });
  }, [clinicianDataArray, sortConfig]);

  // Handle sort click
  const handleSort = useCallback(
    (key) => {
      const direction =
        sortConfig?.key === key && sortConfig.direction === 'asc'
          ? 'desc'
          : 'asc';
      setSortConfig({ key, direction });

      // If this is the first sort or user is explicitly sorting, enable cross-cache sorting
      if (!isSortingAcrossCache) {
        setIsSortingAcrossCache(true);
      }
    },
    [sortConfig, isSortingAcrossCache],
  );

  // Get current page data for display
  // The pages the table offers: the fetched pages, or -- when paging across
  // the cache -- as many as the clinicians left need, on the same page size.
  const displayPagination = (() => {
    if (!pagination || !pageAcrossCache) return pagination;
    const pageSizeForDisplay = pagination.page_size || pageSize;
    const totalPages = Math.max(1, Math.ceil(sortedClinicians.length / pageSizeForDisplay));
    const currentPage = Math.min(Math.max(1, pagination.current_page || 1), totalPages);
    return {
      ...pagination,
      page_size: pageSizeForDisplay,
      current_page: currentPage,
      total_pages: totalPages,
      has_next_page: currentPage < totalPages,
      has_previous_page: currentPage > 1,
    };
  })();

  const getCurrentPageData = () => {
    // Paging across the cache: slice the sorted, filtered clinicians.
    if (pageAcrossCache && displayPagination) {
      const { current_page, page_size } = displayPagination;
      const startIndex = (current_page - 1) * page_size;
      const endIndex = startIndex + page_size;
      return sortedClinicians.slice(startIndex, endIndex);
    }

    // Otherwise, return all sorted data (which is just current page data)
    return sortedClinicians;
  };

  const currentPageClinicians = getCurrentPageData();

  // A load can take minutes now that it runs as a background job; only the
  // newest request may update the page.
  const latestAnalyticsRequest = useRef(0);

  // Handler for fetching analytics data
  const fetchUserAnalytics = useCallback(
    (page = 1, forceRefresh = false) => {
      const requestId = ++latestAnalyticsRequest.current;
      const cacheKey = getCacheKey(
        t1,
        t2,
        page,
        pageSize,
        selectedLabels,
        selectedUserEmails,
        selectedRuleSet,
        noteSource,
      );

      // Check if data is already cached and not forcing refresh
      if (!forceRefresh && dataCache[cacheKey]) {
        setRsp({ Status: 'Data loaded from cache', data: dataCache[cacheKey] });
        setPagination(dataCache[cacheKey].pagination);
        setHasData(true);
        // Pass data to parent
        if (onDataLoaded) {
          onDataLoaded(dataCache[cacheKey].pagination.total_users);
        }
        return;
      }

      setIsLoading(true);
      setRsp({ Status: 'Fetching analytics data...' });
      apiClient
        .getUserAnalytics(
          t1.toISOString(),
          t2.toISOString(),
          page,
          pageSize,
          selectedLabels,
          selectedUserEmails,
          selectedRuleSet,
          noteSource,
        )
        .then((response) => {
          if (requestId !== latestAnalyticsRequest.current) return;
          // Check if response contains an error
          if (response.error) {
            setRsp({ Status: `Error: ${response.error}` });
            setIsLoading(false);
          } else {
            // Cache the response
            setDataCache((prevCache) => ({
              ...prevCache,
              [cacheKey]: response,
            }));
            setRsp({ Status: 'Data fetched successfully', data: response });
            setPagination(response.pagination);
            setHasData(true);
            setIsLoading(false);
            // Pass data to parent
            if (onDataLoaded) {
              onDataLoaded(response.pagination.total_users);
            }

            // Auto-load remaining pages if this is page 1 and there are more pages
            if (page === 1 && response.pagination.has_next_page) {
              console.log(
                'Page 1 loaded, starting auto-load for remaining pages...',
              );
              console.log('response.pagination:', response.pagination);
              console.log('has_next_page:', response.pagination.has_next_page);
              // Pass the response data directly instead of relying on rsp.data
              autoLoadAllPagesWithData(response, forceRefresh);
            }
          }
        })
        .catch((error) => {
          if (requestId !== latestAnalyticsRequest.current) return;
          console.error('Error fetching analytics:', error);
          setRsp({ Status: `Error fetching analytics. - ${error}` });
          setIsLoading(false);
        });
    },
    [
      apiClient,
      t1,
      t2,
      pageSize,
      dataCache,
      getCacheKey,
      onDataLoaded,
      autoLoadAllPagesWithData,
      selectedLabels,
      selectedUserEmails,
      selectedRuleSet,
      noteSource,
    ],
  );

  // Handle page change
  const handlePageChange = useCallback(
    (page) => {
      const cacheKey = getCacheKey(
        t1,
        t2,
        page,
        pageSize,
        selectedLabels,
        selectedUserEmails,
        selectedRuleSet,
        noteSource,
      );
      const isPageCached = !!dataCache[cacheKey];

      // Only reset sorting if navigating to a new page that requires API call
      if (!isPageCached) {
        setSortConfig(null);
        setIsSortingAcrossCache(false);
      }

      fetchUserAnalytics(page);
    },
    [
      fetchUserAnalytics,
      getCacheKey,
      t1,
      t2,
      pageSize,
      dataCache,
      selectedLabels,
      selectedUserEmails,
      selectedRuleSet,
      noteSource,
    ],
  );

  // Handle form submission
  // Stable identity on purpose. As an inline arrow this prop changed on every
  // render, so the panel's completion effect re-ran, refetched analytics,
  // re-rendered, and looped -- ~17k requests before it was caught.
  // Put a run's stored report on screen (COM-564): the filters it was built
  // with go back into the form, and its clinicians become cached query pages,
  // so paging, sorting and the person cards work as for a live query.
  const applyStoredReport = useCallback((stored, jobId) => {
    const filters = stored?.filters || {};
    const startDate = parseRunDate(filters.start_date);
    const endDate = parseRunDate(filters.end_date);
    if (!startDate || !endDate || !stored?.report) {
      throw new Error('The saved report is incomplete.');
    }
    const labels = Array.isArray(filters.labels) ? filters.labels : [];
    const userEmails = Array.isArray(filters.user_emails) ? filters.user_emails : [];
    const ruleSet = filters.export_rule_set || '';
    const source = filters.source || 'all';
    const pages = buildReportPages(stored.report, pageSize);
    const cache = {};
    pages.forEach((reportPage, index) => {
      // t1 is the end of the range and t2 the start, as everywhere here.
      cache[getCacheKey(endDate, startDate, index + 1, pageSize, labels,
        userEmails, ruleSet, source)] = reportPage;
    });
    setDataCache(cache);
    setPersonCardCache({});
    setSelectedLabels(labels);
    setSelectedUserEmails(userEmails);
    setSelectedRuleSet(ruleSet);
    setNoteSource(source);
    setDatePreset(null);
    setT2(startDate);
    setT1(endDate);
    setSortConfig(null);
    setIsSortingAcrossCache(false);
    setAutoLoadError(null);
    setNoteTypeSortConfig(null);
    setIsNoteTypeSummaryOpen(true);
    setAnalyticsActiveTab('clinicians');
    setRsp({ Status: 'Report loaded', data: pages[0] });
    setPagination(pages[0].pagination);
    setReportInfo({
      jobId,
      builtAt: stored.builtAt,
      filters,
      perClinicianNoteTypes: stored.report.per_clinician_note_types || null,
      hasGradeableCounts: reportHasGradeableCounts(stored.report),
    });
    setReportGroupFilter([]);
    setHasData(true);
    if (onDataLoaded) onDataLoaded(pages[0].pagination.total_users);
  }, [getCacheKey, onDataLoaded, pageSize]);

  // Fetch and show a run's stored report. Resolves 'opened', or why not: the
  // run's status ('unavailable' = finished with no report) or 'error'. Every
  // reason but 'unavailable' is put on screen here.
  const loadStoredReport = useCallback(async (jobId) => {
    const requestId = ++latestAnalyticsRequest.current;
    setIsLoading(true);
    setScoreAllRunError('');
    try {
      const stored = await apiClient.getScoreAllReport(jobId);
      if (requestId !== latestAnalyticsRequest.current) return 'superseded';
      if (stored?.status !== 'ready') {
        if (stored?.status === 'queued' || stored?.status === 'grading') {
          setScoreAllRunError(
            'This run is still going. Its report opens here when it finishes.',
          );
        } else if (stored?.status !== 'unavailable') {
          setScoreAllRunError(stored?.error || "We couldn't open this report. Try again.");
        }
        return stored?.status || 'error';
      }
      applyStoredReport(stored, jobId);
      return 'opened';
    } catch (error) {
      if (requestId !== latestAnalyticsRequest.current) return 'superseded';
      console.error('Unable to load the saved report:', error);
      setScoreAllRunError(
        error?.serverMessage || "We couldn't open this report. Try again.",
      );
      return 'error';
    } finally {
      if (requestId === latestAnalyticsRequest.current) setIsLoading(false);
    }
  }, [apiClient, applyStoredReport]);

  const handleScoreAllRunComplete = useCallback((run) => {
    setDownloadJobId(run.exportKey ? run.jobId : null);
    setDownloadStatus(run.exportKey ? 'ready' : 'new');
    if (run.exportError) toast.error(run.exportError);
    setScoreAllRunsKey((key) => key + 1);
    // The run changed scores, so cached clinician cards are stale too.
    setPersonCardCache({});
    if (run.reportKey) {
      loadStoredReport(run.jobId);
    } else {
      setScoreAllRunError(
        run.reportError || 'The analytics report could not be built.',
      );
    }
  }, [loadStoredReport]);

  const handleFormSubmit = useCallback(
    (e) => {
      e.preventDefault();
      setDownloadJobId(null);
      setDownloadStatus('new');
      // Clear cache when date range changes
      setDataCache({});
      setPersonCardCache({});
      setAutoLoadError(null);
      setNoteTypeSortConfig(null);
      setIsNoteTypeSummaryOpen(true);
      setAnalyticsActiveTab('clinicians');
      setReportInfo(null);
      setScoreAllRunError('');
      if (reportMode) {
        // Supersede a report still loading from an earlier View or run.
        latestAnalyticsRequest.current += 1;
        setIsLoading(false);
        // COM-564: the whole report is built on the scoring runner (which
        // also grades anything in range never graded) and opens here when it
        // finishes. Nothing is computed on the Lambda.
        scoreAllRef.current?.start();
        return;
      }
      fetchUserAnalytics(1, true);
    },
    [fetchUserAnalytics, reportMode],
  );

  // Handle new search
  const handleNewSearch = useCallback(() => {
    scoreAllRef.current?.reset();
    latestAnalyticsRequest.current += 1;
    // The superseded load no longer clears this itself.
    setIsLoading(false);
    setReportInfo(null);
    setReportGroupFilter([]);
    setScoreAllRunError('');
    setDownloadJobId(null);
    setDownloadStatus('new');
    setHasData(false);
    setRsp({ Status: 'Response will show here.' });
    setPagination(null);
    setAutoLoadError(null);
    setNoteTypeSortConfig(null);
    setIsNoteTypeSummaryOpen(true);
    setAnalyticsActiveTab('clinicians');
    setExpandedRows({});
    setExpandedRowCarouselIndex({});
    setExpandedRowFeedback({});
    expandedRowFeedbackRequests.current.clear();
  }, []);

  // "View" on a past run (COM-564): open the report that run stored, exactly
  // as it was built -- same dates, groups, clinicians, rule set and note
  // source. It used to rerun live analytics with whatever the form held.
  const loadScoreAllRunResult = useCallback(
    async (jobId, runFilters) => {
      if (!jobId) return;
      // A run started earlier keeps going (it stays in Recent exports), but
      // its completion must not replace the report opened here.
      scoreAllRef.current?.reset();
      setDownloadJobId(jobId);
      setDownloadStatus('checking');
      setLoadingScoreAllRun(true);
      try {
        let opened = await loadStoredReport(jobId);
        if (opened === 'unavailable') {
          // A run from before reports were saved (or whose build failed):
          // its notes are graded, so build just the report and open it when
          // the runner finishes. The panel follows it like any run.
          try {
            const build = await apiClient.buildScoreAllReport(jobId);
            if (build?.error) throw new Error(build.error);
            if (build?.status === 'ready') {
              opened = await loadStoredReport(jobId);
            } else if (['queued', 'grading'].includes(build?.status)) {
              scoreAllRef.current?.track({ ...build, jobId });
            } else {
              setScoreAllRunError("This run's report can't be built. Generate Analytics to rerun it.");
            }
          } catch (buildError) {
            console.error('Unable to build the saved report:', buildError);
            setScoreAllRunError(
              buildError?.serverMessage || "We couldn't build this run's report. Try again.",
            );
          }
        }
        try {
          const status = await apiClient.getScoreAllStatus(jobId);
          if (status?.status === 'completed' && status?.exportKey) {
            setDownloadStatus('ready');
          } else if (status?.status === 'queued' || status?.status === 'grading') {
            setDownloadStatus('preparing');
          } else {
            setDownloadJobId(null);
            setDownloadStatus('new');
          }
        } catch {
          setDownloadStatus('check');
        }
        if (opened !== 'opened' && runFilters) {
          // Nothing stored (an older run): put its filters in the form so
          // Generate Analytics builds the report for exactly this run.
          const startDate = parseRunDate(runFilters.start_date);
          const endDate = parseRunDate(runFilters.end_date);
          if (startDate && endDate) {
            setSelectedLabels(Array.isArray(runFilters.labels) ? runFilters.labels : []);
            setSelectedUserEmails(
              Array.isArray(runFilters.user_emails) ? runFilters.user_emails : [],
            );
            setSelectedRuleSet(runFilters.export_rule_set || '');
            setNoteSource(runFilters.source || 'all');
            setDatePreset(null);
            setT2(startDate);
            setT1(endDate);
          }
        }
      } finally {
        setLoadingScoreAllRun(false);
      }
    },
    [apiClient, loadStoredReport],
  );

  // Download the emailed CSV without running the analytics filters.
  useEffect(() => {
    if (!deepLinkJobId) return;
    let cancelled = false;
    apiClient.getScoreAllExport(deepLinkJobId).then((exported) => {
      if (cancelled) return;
      if (exported?.status === 'ready' && exported.url) {
        window.location.assign(exported.url);
      } else if (exported?.status === 'failed' || exported?.error) {
        toast.error(exported.error || 'The export did not finish. Please open the email link again to retry.');
      } else {
        toast.info("This file is still being prepared. Generate Analytics to track its progress.");
      }
    }).catch(() => {
      if (!cancelled) {
        toast.error('Unable to download the visit CSV. Please open the email link again to retry.');
      }
    });
    return () => { cancelled = true; };
  }, [apiClient, deepLinkJobId]);

  // Fetch users with labels on mount
  useEffect(() => {
    fetchUsersWithLabels();
  }, [fetchUsersWithLabels]);

  // Function to fetch visit types breakdown
  const fetchVisitTypes = useCallback(
    async (userEmail) => {
      if (!userEmail || !t1 || !t2) return;
      
      const cacheKey = visitTypesCacheKey(userEmail);
      if (personCardCache[cacheKey]?.visitTypes) {
        setVisitTypes(personCardCache[cacheKey].visitTypes);
        return;
      }

      setLoadingVisitTypes(true);
      try {
        const response = await apiClient.getVisitTypeBreakdown(
          userEmail,
          queryT1,
          queryT2,
          noteSource,
          selectedRuleSet,
        );
        if (response && !response.error && response.visit_types) {
          setVisitTypes(response.visit_types);
          setPersonCardCache((prev) => ({
            ...prev,
            [cacheKey]: { visitTypes: response.visit_types },
          }));
        } else {
          setVisitTypes([]);
        }
      } catch (error) {
        console.error('Error fetching visit types:', error);
        setVisitTypes([]);
      } finally {
        setLoadingVisitTypes(false);
      }
    },
    [apiClient, queryT1, queryT2, personCardCache, noteSource, selectedRuleSet, visitTypesCacheKey],
  );

  // Built in one place because the note type modal reads this cache after the
  // person card has closed and wiped visitsList -- if the two key formats drift,
  // that modal silently shows "no visits found".
  const getVisitsListCacheKey = useCallback(
    (userEmail) =>
      `visits_list_${userEmail}_${queryT1}_${queryT2}_${selectedRuleSet}_${noteSource}`,
    [queryT1, queryT2, selectedRuleSet, noteSource],
  );

  // Function to fetch visits list
  const fetchVisitsList = useCallback(
    async (userEmail) => {
      if (!userEmail || !t1 || !t2) return;

      const cacheKey = getVisitsListCacheKey(userEmail);
      if (personCardCache[cacheKey]?.visitsList) {
        setVisitsList(personCardCache[cacheKey].visitsList);
        return;
      }

      setLoadingVisitsList(true);
      try {
        const response = await apiClient.getUserVisits(
          userEmail,
          queryT1,
          queryT2,
          selectedRuleSet,
          noteSource,
        );
        if (response && !response.error && response.visits) {
          setVisitsList(response.visits);
          setPersonCardCache((prev) => ({
            ...prev,
            [cacheKey]: { visitsList: response.visits },
          }));
        } else {
          setVisitsList([]);
        }
      } catch (error) {
        console.error('Error fetching visits list:', error);
        setVisitsList([]);
      } finally {
        setLoadingVisitsList(false);
      }
    },
    [apiClient, queryT1, queryT2, personCardCache, selectedRuleSet, noteSource, getVisitsListCacheKey],
  );

  const getExpandedRowFeedbackKey = useCallback(
    (email, noteType) =>
      JSON.stringify([
        email,
        noteType.config_name,
        queryT1,
        queryT2,
        selectedRuleSet,
        noteSource,
      ]),
    [queryT1, queryT2, selectedRuleSet, noteSource],
  );

  // Fetch data when person card opens
  useEffect(() => {
    if (personCardModalOpen && selectedPerson) {
      fetchVisitTypes(selectedPerson.email);
      fetchVisitsList(selectedPerson.email);
    } else {
      // Reset when modal closes
      setVisitTypes([]);
      setVisitsList([]);
    }
  }, [personCardModalOpen, selectedPerson, fetchVisitTypes, fetchVisitsList]);

  // Reset the selected drill-down detail when note type changes
  useEffect(() => {
    setSelectedRuleEvaluation(null);
  }, [selectedNoteType]);

  useEffect(() => {
    if (!selectedRuleEvaluation) return undefined;

    const closeRuleEvaluation = (event) => {
      if (event.key === 'Escape') {
        setSelectedRuleEvaluation(null);
        return;
      }

      if (
        event.type === 'mousedown' &&
        !event.target.closest('[data-rule-evaluation-trigger]') &&
        !ruleEvaluationPopoverRef.current?.contains(event.target)
      ) {
        setSelectedRuleEvaluation(null);
      }
    };

    document.addEventListener('keydown', closeRuleEvaluation);
    document.addEventListener('mousedown', closeRuleEvaluation);
    return () => {
      document.removeEventListener('keydown', closeRuleEvaluation);
      document.removeEventListener('mousedown', closeRuleEvaluation);
    };
  }, [selectedRuleEvaluation]);

  // Fetch feedback for all visit types when a row is expanded
  useEffect(() => {
    Object.entries(expandedRows).forEach(([email, isExpanded]) => {
      if (isExpanded) {
        // Initialize carousel index to 0 if not set
        if (expandedRowCarouselIndex[email] === undefined) {
          setExpandedRowCarouselIndex(prev => ({
            ...prev,
            [email]: 0
          }));
        }
        
        const rowCacheKey = visitTypesCacheKey(email);
        const cached = personCardCache[rowCacheKey];
        const rowVisitTypes = cached?.visitTypes || [];
        
        // Fetch one aggregate summary per note type and prevent duplicate
        // requests while slow AI responses are still in flight.
        rowVisitTypes.forEach((noteType) => {
          const feedbackKey = getExpandedRowFeedbackKey(email, noteType);
          if (
            !expandedRowFeedback[feedbackKey] &&
            !expandedRowFeedbackRequests.current.has(feedbackKey)
          ) {
            expandedRowFeedbackRequests.current.add(feedbackKey);
            apiClient.aggregateFeedback(email, queryT1, queryT2, {
              config_name: noteType.config_name,
              rule_set: selectedRuleSet || undefined,
              source: noteSource,
            })
              .then((response) => {
                if (response && !response.error) {
                  setExpandedRowFeedback(prev => ({
                    ...prev,
                    [feedbackKey]: response
                  }));
                } else {
                  setExpandedRowFeedback(prev => ({
                    ...prev,
                    [feedbackKey]: {
                      error: response?.error || 'Unable to load summary.',
                    },
                  }));
                }
              })
              .catch((error) => {
                console.error('Error fetching aggregated feedback:', error);
                setExpandedRowFeedback(prev => ({
                  ...prev,
                  [feedbackKey]: { error: 'Unable to load summary.' },
                }));
              })
              .finally(() => {
                expandedRowFeedbackRequests.current.delete(feedbackKey);
              });
          }
        });
      }
    });
  }, [
    expandedRows,
    personCardCache,
    queryT1,
    queryT2,
    apiClient,
    expandedRowFeedback,
    getExpandedRowFeedbackKey,
    expandedRowCarouselIndex,
    selectedRuleSet,
    noteSource,
  ]);

  // What this report covers, for file names and the results header: the
  // groups, rule set and note source it was built with (COM-564).
  const ruleSetLabel = (
    ruleSetOptions.find((option) => option.value === selectedRuleSet) ||
    RULE_SET_OPTIONS.find((option) => option.value === selectedRuleSet)
  )?.label || 'All rule sets';
  const reportFilenameBase = () => {
    const parts = [
      ...(selectedLabels.length > 0 ? selectedLabels.slice(0, 3) : ['all-groups']),
      selectedLabels.length > 3 ? `plus-${selectedLabels.length - 3}` : '',
      selectedUserEmails.length > 0 ? `${selectedUserEmails.length}-clinicians` : '',
      selectedRuleSet ? ruleSetLabel : '',
      noteSource !== 'all' ? noteSource : '',
      groupFilterActive ? `filtered-${reportGroupFilter.slice(0, 2).join('-')}` : '',
      `${localDateStamp(t2)}_to_${localDateStamp(t1)}`,
    ];
    return parts.map(filenamePart).filter(Boolean).join('_');
  };

  // Clinician Details as CSV: one row per clinician, under a summary.
  const handleDownloadAnalytics = useCallback(() => {
    try {
      const aggregatedData = getAggregatedTotalData();
      if (!aggregatedData) {
        alert('No data available to download');
        return;
      }

      const allClinicians = getAllCachedClinicianData();
      // Average score per note type, for the note types that carry most of
      // the scored visits (1% or more, at most 15).
      const noteTypesForColumns = (() => {
        if (!perClinicianNoteTypes) return [];
        const all = getAggregatedNoteTypeData();
        const scored = all.reduce((sum, nt) => sum + (Number(nt.visits_with_score) || 0), 0);
        return all
          .filter((nt) => scored > 0 && (Number(nt.visits_with_score) || 0) / scored >= 0.01)
          .sort((a, b) => b.visits_with_score - a.visits_with_score)
          .slice(0, 15);
      })();
      const headers = [
        'Email',
        'Visits',
        'Graded Notes',
        ...(showGradeable ? ['Gradeable Notes'] : []),
        'Time Saved (Hrs)',
        'Most Active Day',
        'Visits on Active Day',
        'Patients Seen',
        'Compliance Grade',
        'Average Compliance Score',
        'Visits Scoring Under 50',
        ...noteTypesForColumns.flatMap((nt) => [
          `${nt.config_name} Average Score`,
          `${nt.config_name} Graded Notes`,
        ]),
      ];
      const summaryRows = [
        ['ANALYTICS SUMMARY'],
        ['Date Range', `${t2.toLocaleDateString()} to ${t1.toLocaleDateString()}`],
        ['Groups', selectedLabels.length > 0 ? selectedLabels.join('; ') : 'All'],
        ['Clinicians Selected', selectedUserEmails.length > 0 ? selectedUserEmails.join('; ') : 'All'],
        ['Rule Set', ruleSetLabel],
        ['Note Source', NOTE_SOURCE_LABELS[noteSource] || noteSource],
        ['Filtered To', groupFilterActive ? reportGroupFilter.join('; ') : 'Everyone in the report'],
        ['Total Clinicians', aggregatedData.totalNumberOfClinicians],
        [
          showNoVisitClinicians
            ? 'Clinicians With No Visits (included)'
            : 'Clinicians With No Visits (not listed)',
          aggregatedData.noVisitClinicians,
        ],
        ['Total Visits', aggregatedData.totalNumberOfConversations],
        ['Total Time Saved (Hrs)', aggregatedData.totalTimeSaved.toFixed(2)],
        ['Total Patients Seen', aggregatedData.totalPatientsSeen],
        [],
        ['CLINICIAN DETAILS'],
        headers,
      ];
      const dataRows = allClinicians.map((clinician) => {
        // No scored visits: blank, not the 0 the API sends.
        const scored = (Number(clinician.evaluatedVisits) || 0) > 0;
        const rows = perClinicianNoteTypes?.clinicians?.[clinician.email] ||
          perClinicianNoteTypes?.clinicians?.[String(clinician.email).toLowerCase()] || [];
        return [
          clinician.email,
          clinician.visits,
          clinician.evaluatedVisits,
          ...(showGradeable ? [clinician.gradeableNotes ?? ''] : []),
          Number(clinician.timeSaved || 0).toFixed(2),
          clinician.activeDay,
          clinician.visitsOnActiveDay,
          clinician.patientsSeen,
          scored ? clinician.avgComplianceScore : '',
          // One decimal: rounded to a whole number, 79.6 read "80" beside a B.
          scored ? (Number(clinician.averageScore) || 0).toFixed(1) : '',
          clinician.fComplianceVisits,
          ...noteTypesForColumns.flatMap((nt) => {
            const row = rows.find((r) => r.note_type_key === nt.note_type_key);
            const evaluated = Number(row?.visits_with_score) || 0;
            return [
              evaluated > 0 ? Math.round(row.total_score / evaluated) : '',
              evaluated || '',
            ];
          }),
        ];
      });

      downloadCsv([...summaryRows, ...dataRows], `clinicians_${reportFilenameBase()}.csv`);
    } catch (error) {
      console.error('Error downloading analytics:', error);
      alert('Failed to download analytics data');
    }
  }, [t1, t2, getAggregatedTotalData, getAllCachedClinicianData, selectedLabels,
    selectedUserEmails, ruleSetLabel, noteSource, reportFilenameBase, showNoVisitClinicians,
    perClinicianNoteTypes, getAggregatedNoteTypeData,
    groupFilterActive, reportGroupFilter, showGradeable]);

  // Performance by Note Type as CSV (COM-564): one row per note type and
  // rule, with the note type's totals on every row, so it filters in Excel.
  const handleDownloadNoteTypes = useCallback(() => {
    try {
      const noteTypes = getAggregatedNoteTypeData();
      if (noteTypes.length === 0) {
        alert('No note type data available to download');
        return;
      }
      const headers = [
        'Note Type',
        'Clinicians',
        'Graded Notes',
        ...(showGradeable ? ['Gradeable Notes'] : []),
        'Average Score',
        'Evaluations Scoring Under 50',
        'Rule Category',
        'Requirement',
        'Fully Met',
        'Partially Met',
        'Not Met',
        'Not Applicable',
        'Times Evaluated',
        'Not Met Rate (%)',
      ];
      const rows = [
        ['PERFORMANCE BY NOTE TYPE'],
        ['Date Range', `${t2.toLocaleDateString()} to ${t1.toLocaleDateString()}`],
        ['Groups', selectedLabels.length > 0 ? selectedLabels.join('; ') : 'All'],
        ['Rule Set', ruleSetLabel],
        ['Note Source', NOTE_SOURCE_LABELS[noteSource] || noteSource],
        ['Filtered To', groupFilterActive ? reportGroupFilter.join('; ') : 'Everyone in the report'],
        [],
        headers,
      ];
      noteTypes.forEach((noteType) => {
        const totals = [
          noteType.config_name,
          noteType.clinician_count,
          noteType.visits_with_score,
          ...(showGradeable ? [noteType.gradeable_notes ?? ''] : []),
          noteType.average_score === null || noteType.average_score === undefined ||
            !(noteType.visits_with_score > 0)
            ? ''
            : Number(noteType.average_score).toFixed(1),
          noteType.failed_visits,
        ];
        const rules = Array.isArray(noteType.rule_results) ? noteType.rule_results : [];
        if (rules.length === 0) {
          rows.push([...totals, '', '', '', '', '', '', '', '']);
          return;
        }
        rules.forEach((rule) => {
          const evaluated =
            (rule.fully_met_count || 0) + (rule.partially_met_count || 0) +
            (rule.not_met_count || 0);
          rows.push([
            ...totals,
            rule.category,
            rule.requirement_text,
            rule.fully_met_count || 0,
            rule.partially_met_count || 0,
            rule.not_met_count || 0,
            rule.not_applicable_count || 0,
            evaluated,
            evaluated > 0 ? ((100 * (rule.not_met_count || 0)) / evaluated).toFixed(1) : '',
          ]);
        });
      });
      downloadCsv(rows, `note_types_${reportFilenameBase()}.csv`);
    } catch (error) {
      console.error('Error downloading note type performance:', error);
      alert('Failed to download note type data');
    }
  }, [t1, t2, getAggregatedNoteTypeData, selectedLabels, ruleSetLabel, noteSource,
    reportFilenameBase, groupFilterActive, reportGroupFilter, showGradeable]);

  // Download the CSV prepared by this report's scoring run.
  const handleDownloadRawCompliance = useCallback(async () => {
    if (!downloadJobId) return;
    setIsExportingVisitDetail(true);
    try {
      const exported = await apiClient.getScoreAllExport(downloadJobId);
      if (exported?.status === 'ready' && exported.url) {
        window.location.assign(exported.url);
      } else {
        toast.error(exported?.error || 'The visit CSV is unavailable. Generate Analytics again to prepare it.');
      }
    } catch (error) {
      console.error('Error exporting raw compliance data:', error);
      toast.error('Failed to download the visit CSV. Please try again.');
    } finally {
      setIsExportingVisitDetail(false);
    }
  }, [apiClient, downloadJobId]);

  const rawComplianceButton = apiClient?.features?.score_all_notes &&
    downloadJobId && downloadStatus === 'ready' && (
      <Button
        color="success"
        size="sm"
        outline
        data-testid="raw-compliance-download"
        onClick={handleDownloadRawCompliance}
        disabled={isExportingVisitDetail}
      >
        <i
          className={`fa ${isExportingVisitDetail ? 'fa-spinner fa-spin' : 'fa-download'}`}
          style={{ marginRight: '4px' }}
        ></i>
        {isExportingVisitDetail ? 'Downloading Visit CSV...' : 'Download Raw Visit CSV'}
      </Button>
    );

  // Render pagination controls
  const renderPagination = () => {
    if (!displayPagination || displayPagination.total_pages === 0) return null;

    const { current_page, total_pages, has_next_page, has_previous_page } =
      displayPagination;
    const showPageControls = total_pages > 1;

    return (
      <div className="pagination-container">
        {showPageControls && (
          <Pagination
            size="sm"
            className="analytics-pagination"
            listClassName="analytics-pagination-list"
            aria-label="Clinician results pages"
          >
            <PaginationItem disabled={!has_previous_page}>
              <PaginationLink
                tag="button"
                type="button"
                className="pagination-direction"
                aria-label="Previous page"
                onClick={() =>
                  has_previous_page && handlePageChange(current_page - 1)
                }
              >
                <i className="fa fa-chevron-left" aria-hidden="true"></i>
                Previous
              </PaginationLink>
            </PaginationItem>

            {Array.from({ length: Math.min(5, total_pages) }, (_, i) => {
              let pageNum;
              if (total_pages <= 5) {
                pageNum = i + 1;
              } else if (current_page <= 3) {
                pageNum = i + 1;
              } else if (current_page >= total_pages - 2) {
                pageNum = total_pages - 4 + i;
              } else {
                pageNum = current_page - 2 + i;
              }

              return (
                <PaginationItem key={pageNum} active={pageNum === current_page}>
                  <PaginationLink
                    tag="button"
                    type="button"
                    aria-label={`Page ${pageNum}`}
                    aria-current={
                      pageNum === current_page ? 'page' : undefined
                    }
                    onClick={() =>
                      pageNum !== current_page && handlePageChange(pageNum)
                    }
                  >
                    {pageNum}
                  </PaginationLink>
                </PaginationItem>
              );
            })}

            <PaginationItem disabled={!has_next_page}>
              <PaginationLink
                tag="button"
                type="button"
                className="pagination-direction"
                aria-label="Next page"
                onClick={() =>
                  has_next_page && handlePageChange(current_page + 1)
                }
              >
                Next
                <i className="fa fa-chevron-right" aria-hidden="true"></i>
              </PaginationLink>
            </PaginationItem>
          </Pagination>
        )}

        <div className="pagination-info">
          Page {current_page} of {total_pages}
          {currentPageClinicians.length > 0 && (
            <span>
              {' '}
              ({currentPageClinicians.length} clinicians on this page)
            </span>
          )}
        </div>
      </div>
    );
  };

  // Render aggregated total data summary
  const renderTotalDataSummary = () => {
    const aggregatedData = getAggregatedTotalData();
    if (!aggregatedData) return null;

    // "Have we got everything yet?" is a question about PAGES, not people.
    //
    // It used to compare clinicians-with-visits against pagination.total_users,
    // but those count different things: total_users is everyone under the
    // subscription matching the filter, while totalNumberOfClinicians only
    // counts the ones who actually documented in the window. Any group with a
    // single clinician who didn't document — so, in practice, every group —
    // looked permanently incomplete, and all four stat cards sat on a spinner
    // that never resolved. That is the "aggregate stats don't work".
    const { loaded: loadedPages, total: totalPages } = loadProgress();
    const totalClinicians = groupFilterActive
      ? Object.keys(groupClinicians()).length
      : rsp.data?.pagination?.total_users || 0;
    const cachedClinicians = aggregatedData.totalNumberOfClinicians;
    const isIncomplete = totalPages > 0 && loadedPages < totalPages;
    const isStillLoading = isAutoLoading || (isIncomplete && !autoLoadError);

    return (
      <>
        <h3 style={{ marginBottom: '1rem', color: '#32325d' }}>
          {autoLoadError ? (
            `Partial Analytics Summary — ${loadedPages} of ${totalPages} pages loaded`
          ) : isStillLoading ? (
            <>
              <i
                className="fa fa-spinner fa-spin"
                style={{ marginRight: '0.5rem', color: '#5e72e4' }}
              ></i>
              Loading Analytics Summary — page {loadedPages} of {totalPages}
            </>
          ) : (
            // Both numbers, said plainly: a clinician with no visits in this
            // window is a real answer, not missing data.
            `Total Analytics Summary — ${cachedClinicians} of ${totalClinicians} clinicians documented`
          )}
        </h3>
        <div className="analytics-summary-grid">
          <div className="stat-card">
            <span className="stat-label"># of Clinicians</span>
            <span className="stat-value">
              {isStillLoading ? (
                <i
                  className="fa fa-spinner fa-spin"
                  style={{ color: '#5e72e4' }}
                ></i>
              ) : (
                aggregatedData.totalNumberOfClinicians
              )}
            </span>
          </div>
          <div className="stat-card">
            <span className="stat-label"># of Visits</span>
            <span className="stat-value">
              {isStillLoading ? (
                <i
                  className="fa fa-spinner fa-spin"
                  style={{ color: '#5e72e4' }}
                ></i>
              ) : (
                aggregatedData.totalNumberOfConversations
              )}
            </span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Time Saved (Hrs)</span>
            <span className="stat-value">
              {isStillLoading ? (
                <i
                  className="fa fa-spinner fa-spin"
                  style={{ color: '#5e72e4' }}
                ></i>
              ) : (
                aggregatedData.totalTimeSaved.toFixed(2)
              )}
            </span>
          </div>
          <div className="stat-card">
            <span className="stat-label"># of Patients Seen</span>
            <span className="stat-value">
              {isStillLoading ? (
                <i
                  className="fa fa-spinner fa-spin"
                  style={{ color: '#5e72e4' }}
                ></i>
              ) : (
                aggregatedData.totalPatientsSeen
              )}
            </span>
          </div>
        </div>
      </>
    );
  };

  const renderNoteTypeSummary = () => {
    const noteTypeData = getAggregatedNoteTypeData();
    if (noteTypeData.length === 0) {
      return (
        <div className="text-center text-muted" style={{ padding: '3rem' }}>
          {isAutoLoading ? (
            <>
              <i
                className="fa fa-spinner fa-spin"
                style={{ color: '#5e72e4', fontSize: '2rem' }}
              ></i>
              <p style={{ marginTop: '0.75rem' }}>
                Loading note type performance...
              </p>
            </>
          ) : (
            <>
              <i
                className="fa fa-file-text"
                style={{ fontSize: '2rem', opacity: 0.3 }}
              ></i>
              <p style={{ marginTop: '0.75rem' }}>
                No note type analytics are available for this period.
              </p>
            </>
          )}
        </div>
      );
    }

    const visibleNoteTypes = noteTypeSortConfig
      ? [...noteTypeData].sort((a, b) => {
          const aValue = a[noteTypeSortConfig.key];
          const bValue = b[noteTypeSortConfig.key];

          if (aValue === null && bValue === null) return 0;
          if (aValue === null) return 1;
          if (bValue === null) return -1;

          const comparison =
            typeof aValue === 'string'
              ? aValue.localeCompare(bValue)
              : aValue - bValue;

          return noteTypeSortConfig.direction === 'asc'
            ? comparison
            : -comparison;
        })
      : noteTypeData;

    const handleNoteTypeSort = (key) => {
      setNoteTypeSortConfig((currentSort) => {
        if (!currentSort || currentSort.key !== key) {
          return { key, direction: 'asc' };
        }
        if (currentSort.direction === 'asc') {
          return { key, direction: 'desc' };
        }
        return null;
      });
    };

    const renderNoteTypeHeader = (label, key) => {
      const isActive = noteTypeSortConfig?.key === key;
      const direction = isActive ? noteTypeSortConfig.direction : null;

      return (
        <th
          aria-sort={
            direction === 'asc'
              ? 'ascending'
              : direction === 'desc'
              ? 'descending'
              : undefined
          }
        >
          <button
            type="button"
            className={`note-type-sort-button${isActive ? ' active' : ''}`}
            onClick={() => handleNoteTypeSort(key)}
          >
            {label}
            <span aria-hidden="true">
              {direction === 'asc' ? '▲' : direction === 'desc' ? '▼' : '↕'}
            </span>
          </button>
        </th>
      );
    };

    return (
      <Card className="note-type-summary-card">
        <CardHeader className="note-type-summary-header">
          <div>
            <h4>Performance by Note Type</h4>
            <p>
              {autoLoadError
                ? `Partial totals — page ${autoLoadError.page} of ${autoLoadError.total} could not be loaded. Update Search to retry.`
                : isAutoLoading
                ? `Loading organization totals (${autoLoadProgress.current}/${autoLoadProgress.total} pages)`
                : 'Scored note types found for the selected clinicians and date range'}
            </p>
          </div>
          <button
            type="button"
            className="note-type-collapse-button"
            aria-controls="note-type-summary-content"
            aria-expanded={isNoteTypeSummaryOpen}
            title={
              isNoteTypeSummaryOpen
                ? 'Collapse note type performance'
                : 'Expand note type performance'
            }
            onClick={() =>
              setIsNoteTypeSummaryOpen((isOpen) => !isOpen)
            }
          >
            <i
              className={`fa ${
                isNoteTypeSummaryOpen
                  ? 'fa-chevron-up'
                  : 'fa-chevron-down'
              }`}
              aria-hidden="true"
            ></i>
            <span className="sr-only">
              {isNoteTypeSummaryOpen ? 'Collapse section' : 'Expand section'}
            </span>
          </button>
        </CardHeader>
        {isNoteTypeSummaryOpen && (
          <div id="note-type-summary-content" className="table-responsive">
            <Table className="note-type-summary-table" hover>
              <thead>
                <tr>
                  {renderNoteTypeHeader('Note Type', 'config_name')}
                  {renderNoteTypeHeader('Clinicians', 'clinician_count')}
                  {renderNoteTypeHeader('Graded Notes', 'visits_with_score')}
                  {renderNoteTypeHeader('Average Score', 'average_score')}
                  {renderNoteTypeHeader(
                    'Failed Evaluations',
                    'failed_visits',
                  )}
                </tr>
              </thead>
              <tbody>
                {visibleNoteTypes.map((noteType) => {
                  const failureRate =
                    noteType.visits_with_score > 0
                      ? (noteType.failed_visits /
                          noteType.visits_with_score) *
                        100
                      : 0;
                  const isExpanded =
                    expandedOrganizationNoteType === noteType.note_type_key;
                  const ruleGaps = (noteType.rule_results || []).filter(
                    (rule) =>
                      rule.not_met_count > 0 ||
                      rule.partially_met_count > 0,
                  );
                  const ruleEvaluationVisits =
                    Number(noteType.rule_evaluation_visits) || 0;
                  const emptyRuleGapMessage =
                    noteType.has_complete_rule_results_data
                      ? 'No failed or partially met rule results are available for this note type.'
                      : ruleEvaluationVisits > 0
                      ? 'No failed or partially met results were found in the available structured rule data.'
                      : 'No structured rule results are available for this note type in the selected date range.';

                  return (
                    <React.Fragment key={noteType.note_type_key}>
                      <tr>
                        <td>
                          <button
                            type="button"
                            className="note-type-row-toggle"
                            aria-expanded={isExpanded}
                            onClick={() =>
                              setExpandedOrganizationNoteType((current) =>
                                current === noteType.note_type_key
                                  ? null
                                  : noteType.note_type_key,
                              )
                            }
                          >
                            <i
                              className={`fa ${
                                isExpanded
                                  ? 'fa-chevron-down'
                                  : 'fa-chevron-right'
                              }`}
                              aria-hidden="true"
                            ></i>
                            <strong>{noteType.config_name}</strong>
                          </button>
                        </td>
                        <td>{noteType.clinician_count}</td>
                        <td>
                          {noteType.visits_with_score}
                          {noteType.gradeable_notes !== undefined &&
                            noteType.gradeable_notes !== null
                            ? ` of ${noteType.gradeable_notes}`
                            : ''}
                        </td>
                        <td>
                          {noteType.average_score === null ||
                          !(noteType.visits_with_score > 0)
                            ? '--'
                            : Number(noteType.average_score).toFixed(1)}
                        </td>
                        <td>
                          {noteType.failed_visits}
                          {noteType.visits_with_score > 0
                            ? ` (${failureRate.toFixed(1)}%)`
                            : ''}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan="5" className="rule-gap-cell">
                            <div className="rule-gap-header">
                              <h6>Rule gaps</h6>
                            </div>
                            {!noteType.has_complete_rule_results_data && (
                              <p className="rule-gap-coverage">
                                Structured rule results are available for{' '}
                                {ruleEvaluationVisits} of{' '}
                                {noteType.visits_with_score} graded notes.
                                Older or unavailable records are excluded.
                              </p>
                            )}
                            {ruleGaps.length > 0 ? (
                              <div className="table-responsive">
                                <Table
                                  className="rule-gap-table"
                                  size="sm"
                                >
                                  <thead>
                                    <tr>
                                      <th>Rule</th>
                                      <th>Category</th>
                                      <th>Not Met</th>
                                      <th>Partial</th>
                                      <th>Evaluated</th>
                                      <th>Not Met Rate</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {ruleGaps.map((rule) => {
                                      const evaluatedCount =
                                        rule.fully_met_count +
                                        rule.partially_met_count +
                                        rule.not_met_count;
                                      const notMetRate =
                                        evaluatedCount > 0
                                          ? (rule.not_met_count /
                                              evaluatedCount) *
                                            100
                                          : 0;

                                      return (
                                        <tr
                                          key={JSON.stringify([
                                            rule.category,
                                            rule.requirement_text,
                                          ])}
                                        >
                                          <td>{rule.requirement_text}</td>
                                          <td>{rule.category || '--'}</td>
                                          <td>{rule.not_met_count}</td>
                                          <td>{rule.partially_met_count}</td>
                                          <td>{evaluatedCount}</td>
                                          <td>{notMetRate.toFixed(1)}%</td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </Table>
                              </div>
                            ) : (
                              <p className="rule-gap-empty">
                                {emptyRuleGapMessage}
                              </p>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}
      </Card>
    );
  };

  // Render expanded row content
  const renderExpandedRowContent = (clinician) => {
    // Get visit types and visits list from cache for this specific email
    const getVisitTypesForEmail = (email) => {
      const cacheKey = visitTypesCacheKey(email);
      const cached = personCardCache[cacheKey];
      return cached?.visitTypes || [];
    };

    const rowVisitTypes = getVisitTypesForEmail(clinician.email);
    const currentIndex = expandedRowCarouselIndex[clinician.email] || 0;

    // Check if data is loading for this specific email
    const clinicianCacheKey = visitTypesCacheKey(clinician.email);
    const hasVisitTypesCache = !!personCardCache[clinicianCacheKey]?.visitTypes;
    const isLoadingData = loadingVisitTypes && !hasVisitTypesCache;

    const handlePrev = () => {
      if (currentIndex > 0) {
        const newIndex = currentIndex - 1;
        setExpandedRowCarouselIndex(prev => ({
          ...prev,
          [clinician.email]: newIndex
        }));
        // Scroll to the previous card
        setTimeout(() => {
          const carousel = document.getElementById(`carousel-${clinician.email}`);
          if (carousel) {
            carousel.scrollLeft = newIndex * 332; // 320px width + 12px gap (0.75rem)
          }
        }, 0);
      }
    };

    const handleNext = () => {
      if (currentIndex < rowVisitTypes.length - 1) {
        const newIndex = currentIndex + 1;
        setExpandedRowCarouselIndex(prev => ({
          ...prev,
          [clinician.email]: newIndex
        }));
        // Scroll to the next card
        setTimeout(() => {
          const carousel = document.getElementById(`carousel-${clinician.email}`);
          if (carousel) {
            carousel.scrollLeft = newIndex * 332; // 320px width + 12px gap (0.75rem)
          }
        }, 0);
      }
    };

    const handlePrint = () => {
      // Create a printable version of the carousel content
      const printWindow = window.open('', '_blank');
      const printContent = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Analytics Report - ${clinician.email}</title>
            <style>
              @media print {
                @page {
                  size: letter;
                  margin: 0.5in;
                }
                body {
                  font-family: Arial, sans-serif;
                  color: #333;
                }
              }
              body {
                font-family: Arial, sans-serif;
                padding: 20px;
                color: #333;
              }
              .header {
                margin-bottom: 30px;
                border-bottom: 2px solid #5e72e4;
                padding-bottom: 15px;
              }
              .header h1 {
                color: #5e72e4;
                margin: 0;
                font-size: 24px;
              }
              .header p {
                margin: 5px 0;
                color: #666;
              }
              .cards-container {
                display: grid;
                grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
                gap: 20px;
                margin-top: 20px;
              }
              .card {
                border: 1px solid #e9ecef;
                border-radius: 8px;
                padding: 15px;
                background: white;
                page-break-inside: avoid;
              }
              .card-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 15px;
                padding-bottom: 10px;
                border-bottom: 2px solid #e9ecef;
              }
              .card-title {
                font-size: 16px;
                font-weight: bold;
                color: #32325d;
                margin: 0;
              }
              .badge {
                padding: 5px 10px;
                border-radius: 4px;
                font-weight: bold;
                font-size: 14px;
              }
              .badge-success { background: #28a745; color: white; }
              .badge-warning { background: #ffc107; color: #333; }
              .badge-danger { background: #dc3545; color: white; }
              .badge-secondary { background: #6c757d; color: white; }
              .score {
                text-align: center;
                margin: 15px 0;
              }
              .score-label {
                font-size: 12px;
                color: #6c757d;
                text-transform: uppercase;
                margin-bottom: 5px;
              }
              .score-value {
                font-size: 32px;
                font-weight: bold;
                color: #5e72e4;
                margin: 5px 0;
              }
              .score-info {
                font-size: 12px;
                color: #6c757d;
              }
              .feedback {
                background: #f8f9ff;
                padding: 12px;
                border-radius: 6px;
                border-left: 3px solid #5e72e4;
                margin-top: 15px;
              }
              .feedback-label {
                font-size: 11px;
                color: #6c757d;
                text-transform: uppercase;
                margin-bottom: 8px;
                font-weight: bold;
              }
              .feedback-text {
                font-size: 13px;
                color: #495057;
                line-height: 1.5;
                margin: 0;
              }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>Analytics Dashboard Report</h1>
              <p><strong>Clinician:</strong> ${clinician.email}</p>
              <p><strong>Date Range:</strong> ${t2.toLocaleDateString()} to ${t1.toLocaleDateString()}</p>
              <p><strong>Total Visits:</strong> ${clinician.visits} | <strong>Time Saved:</strong> ${clinician.timeSaved.toFixed(2)} hours | <strong>Average Score:</strong> ${clinician.averageScore || '--'}</p>
            </div>
            <div class="cards-container">
              ${rowVisitTypes.map((noteType, idx) => {
                const noteFeedbackKey = getExpandedRowFeedbackKey(
                  clinician.email,
                  noteType,
                );
                const noteFeedback = expandedRowFeedback[noteFeedbackKey];
                const displayGrade = getNoteTypeDisplayGrade(noteType);
                const badgeClass = `badge-${getNoteTypeGradeColor(noteType)}`;
                return `
                  <div class="card">
                    <div class="card-header">
                      <h3 class="card-title">${noteType.config_name || 'Unknown'}</h3>
                      <span class="badge ${badgeClass}">${displayGrade}</span>
                    </div>
                    <div class="score">
                      <div class="score-label">Average Score</div>
                      <div class="score-value">${getNoteTypeAverageScore(noteType)}</div>
                      <div class="score-info">${getNoteTypeEvaluationSummary(noteType)}</div>
                    </div>
                    <div class="feedback">
                      <div class="feedback-label">Summary</div>
                      <div class="feedback-text">${noteFeedback && noteFeedback.aggregated_insights ? noteFeedback.aggregated_insights : noteFeedback && noteFeedback.error ? 'Unable to load summary.' : 'Loading feedback...'}</div>
                    </div>
                  </div>
                `;
              }).join('')}
            </div>
          </body>
        </html>
      `;
      printWindow.document.write(printContent);
      printWindow.document.close();
      setTimeout(() => {
        printWindow.print();
      }, 250);
    };

    return (
      <div style={{ padding: '0.75rem', backgroundColor: '#f8f9ff' }}>
        {/* Print Button and Info */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.625rem' }}>
          <div>
            <h6
              style={{
                color: '#32325d',
                fontSize: '0.875rem',
                fontWeight: '700',
                margin: '0 0 0.15rem',
              }}
            >
              Note Type Performance
            </h6>
            <div style={{ fontSize: '0.75rem', color: '#7b8497' }}>
              Select a note type to inspect visit-level results.
            </div>
          </div>
          <Button
            color="outline-secondary"
            onClick={handlePrint}
            style={{
              background: 'white',
              border: '1px solid #d8dce5',
              borderRadius: '4px',
              color: '#525f7f',
              padding: '0.375rem 0.625rem',
              fontSize: '0.75rem',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '0.375rem',
            }}
            title="Print will show everything in this card (The how to improve on each note type)"
          >
            <i className="fa fa-print"></i>
            Print
          </Button>
        </div>
        
        {/* Horizontal Carousel Container */}
        {isLoadingData ? (
          <div className="text-center" style={{ padding: '3rem' }}>
            <i className="fa fa-spinner fa-spin fa-3x" style={{ color: '#5e72e4' }}></i>
            <p style={{ marginTop: '1rem', color: '#6c757d' }}>Loading note types...</p>
          </div>
        ) : rowVisitTypes.length > 0 ? (
          <div style={{ position: 'relative' }}>
            {/* Carousel Navigation - Previous Button */}
            {currentIndex > 0 && (
              <button
                type="button"
                aria-label="Previous note type"
                onClick={handlePrev}
                style={{
                  position: 'absolute',
                  left: '-12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'transparent',
                  border: 0,
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  zIndex: 10,
                  padding: 0,
                }}
              >
                <i className="fa fa-chevron-left" style={{ color: '#5e72e4', fontSize: '1rem' }}></i>
              </button>
            )}

            {/* Carousel Content */}
            <div
              id={`carousel-${clinician.email}`}
              style={{
                display: 'flex',
                overflowX: 'auto',
                scrollBehavior: 'smooth',
                gap: '0.75rem',
                padding: '0.25rem 0',
                position: 'relative',
                scrollSnapType: 'x mandatory',
                WebkitOverflowScrolling: 'touch',
              }}
            >
              {rowVisitTypes.map((noteType, idx) => {
                const isActive = idx === currentIndex;
                const noteFeedbackKey = getExpandedRowFeedbackKey(
                  clinician.email,
                  noteType,
                );
                const noteFeedback = expandedRowFeedback[noteFeedbackKey];
                const openNoteTypeDetails = () => {
                  setSelectedNoteType(noteType.config_name);
                  setSelectedPerson(clinician);
                  fetchVisitsList(clinician.email);
                  setNoteTypeModalOpen(true);
                };

                return (
                  <div
                    key={idx}
                    onClick={openNoteTypeDetails}
                    style={{
                      minWidth: '320px',
                      maxWidth: '320px',
                      background: 'white',
                      borderRadius: '8px',
                      padding: '0.75rem',
                      boxShadow: isActive 
                        ? '0 2px 8px rgba(94, 114, 228, 0.14)'
                        : 'none',
                      border: isActive ? '1px solid #5e72e4' : '1px solid #e3e6ec',
                      cursor: 'pointer',
                      transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                      scrollSnapAlign: 'start',
                      flexShrink: 0,
                      textAlign: 'left',
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.borderColor = '#aeb9ef';
                        e.currentTarget.style.boxShadow =
                          '0 2px 8px rgba(94, 114, 228, 0.1)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.borderColor = '#e3e6ec';
                        e.currentTarget.style.boxShadow = 'none';
                      }
                    }}
                  >
                    {/* Note Type Header */}
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '0.5rem',
                        marginBottom: '0.5rem',
                        paddingBottom: '0.5rem',
                        borderBottom: '1px solid #e9ecef',
                      }}
                    >
                      <h4
                        style={{
                          color: '#32325d',
                          fontWeight: '700',
                          flex: 1,
                          margin: 0,
                          fontSize: '0.925rem',
                          letterSpacing: '-0.01em',
                          lineHeight: 1.25,
                          minWidth: 0,
                          textAlign: 'left',
                        }}
                      >
                        {noteType.config_name || 'Unknown'}
                      </h4>
                      <Badge
                        color={getNoteTypeGradeColor(noteType)}
                        style={{
                          fontSize: '0.75rem',
                          padding: '0.25rem 0.5rem',
                          fontWeight: '700',
                          borderRadius: '999px',
                        }}
                      >
                        {getNoteTypeDisplayGrade(noteType)}
                      </Badge>
                    </div>

                    {/* Average Score */}
                    <div
                      style={{
                        alignItems: 'flex-end',
                        borderBottom: '1px solid #eef0f4',
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: '0.625rem',
                        paddingBottom: '0.5rem',
                      }}
                    >
                      <div style={{ textAlign: 'left' }}>
                        <div
                          style={{
                            fontSize: '0.65rem',
                            color: '#6c757d',
                            fontWeight: '600',
                            textTransform: 'uppercase',
                            letterSpacing: '0.4px',
                            marginBottom: '0.1rem',
                          }}
                        >
                          Average Score
                        </div>
                        <div
                          style={{
                            color: '#5e72e4',
                            fontSize: '1.5rem',
                            fontWeight: '700',
                            lineHeight: 1,
                          }}
                        >
                          {getNoteTypeAverageScore(noteType)}
                        </div>
                      </div>
                      <div
                        style={{
                          color: '#6c757d',
                          fontSize: '0.7rem',
                          lineHeight: 1.3,
                          maxWidth: '175px',
                          textAlign: 'right',
                        }}
                      >
                        {getNoteTypeEvaluationSummary(noteType)}
                      </div>
                    </div>

                    {/* Authorship breakdown (COM-390 "Score All Notes").
                        Plain muted text, deliberately not another colored
                        badge -- this is a count, not a quality judgment.
                        Silently shows nothing until the backend sends
                        these counts. */}
                    {noteType.has_authorship_data && (
                      <div
                        className="text-muted"
                        style={{ fontSize: '0.7rem', marginBottom: '0.5rem' }}
                      >
                        {noteType.ai_authored_count ?? 0} AI-authored ·{' '}
                        {noteType.clinician_authored_count ?? 0} clinician-authored
                      </div>
                    )}

                    {/* AI Summary / Feedback */}
                    <div
                      style={{
                        maxHeight: '240px',
                        overflowY: 'auto',
                      }}
                    >
                      <div
                        style={{
                          fontSize: '0.7rem',
                          color: '#5e72e4',
                          fontWeight: '700',
                          textTransform: 'uppercase',
                          letterSpacing: '0.4px',
                          marginBottom: '0.4rem',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <i className="fa fa-robot" style={{ marginRight: '0.35rem', fontSize: '0.8rem' }}></i>
                        Summary
                      </div>
                      {noteFeedback?.error ? (
                        <div
                          style={{
                            color: '#6c757d',
                            fontSize: '0.75rem',
                            padding: '0.5rem 0',
                            textAlign: 'center',
                          }}
                        >
                          <div style={{ marginBottom: '0.5rem' }}>
                            Unable to load summary.
                          </div>
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              expandedRowFeedbackRequests.current.delete(
                                noteFeedbackKey,
                              );
                              setExpandedRowFeedback((currentFeedback) => {
                                const nextFeedback = { ...currentFeedback };
                                delete nextFeedback[noteFeedbackKey];
                                return nextFeedback;
                              });
                            }}
                            style={{
                              background: 'transparent',
                              border: 0,
                              color: '#5e72e4',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              padding: 0,
                              textDecoration: 'underline',
                            }}
                          >
                            Retry
                          </button>
                        </div>
                      ) : noteFeedback && noteFeedback.aggregated_insights ? (
                        <div
                          style={{
                            margin: 0,
                            color: '#32325d',
                            lineHeight: '1.4',
                            fontSize: '0.8rem',
                            textAlign: 'left',
                          }}
                        >
                          {formatAggregatedFeedback(noteFeedback.aggregated_insights).map((point, idx) => (
                            <div
                              key={idx}
                              style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                marginBottom: '0.35rem',
                                textAlign: 'left',
                                width: '100%',
                              }}
                            >
                              <span
                                style={{
                                  color: '#5e72e4',
                                  marginRight: '0.4rem',
                                  marginTop: '0.35rem',
                                  fontSize: '0.5rem',
                                  flexShrink: 0,
                                  fontWeight: 'bold',
                                  lineHeight: '1',
                                }}
                              >
                                ●
                              </span>
                              <span style={{ 
                                flex: 1, 
                                textAlign: 'left',
                                wordWrap: 'break-word',
                              }}>
                                {point}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{ textAlign: 'center', padding: '0.5rem 0' }}>
                          <i className="fa fa-spinner fa-spin" style={{ color: '#5e72e4', fontSize: '0.875rem', marginBottom: '0.25rem' }}></i>
                          <p style={{ margin: 0, color: '#6c757d', fontSize: '0.7rem' }}>
                            Loading feedback...
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Carousel Navigation - Next Button */}
            {currentIndex < rowVisitTypes.length - 1 && (
              <button
                type="button"
                aria-label="Next note type"
                onClick={handleNext}
                style={{
                  position: 'absolute',
                  right: '-12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'transparent',
                  border: 0,
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  zIndex: 10,
                  padding: 0,
                }}
              >
                <i className="fa fa-chevron-right" style={{ color: '#5e72e4', fontSize: '1rem' }}></i>
              </button>
            )}

            {/* Carousel Indicators */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '0.1rem',
                marginTop: '0.75rem',
              }}
            >
              {rowVisitTypes.map((_, idx) => (
                <button
                  key={idx}
                  type="button"
                  aria-label={`Show note type ${idx + 1}`}
                  aria-current={idx === currentIndex ? 'true' : undefined}
                  onClick={() => {
                    setExpandedRowCarouselIndex(prev => ({
                      ...prev,
                      [clinician.email]: idx
                    }));
                  }}
                  style={{
                    alignItems: 'center',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    height: '24px',
                    justifyContent: 'center',
                    padding: 0,
                    width: '24px',
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      background:
                        idx === currentIndex ? '#5e72e4' : '#e9ecef',
                      borderRadius: '3px',
                      height: '6px',
                      transition: 'all 0.2s ease',
                      width: idx === currentIndex ? '18px' : '6px',
                    }}
                  ></span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center text-muted" style={{ padding: '3rem' }}>
            <i
              className="fa fa-file-text"
              style={{ fontSize: '3rem', opacity: 0.3, marginBottom: '1rem' }}
            ></i>
            <p>
              {clinician.evaluatedVisits === 0
                ? 'No graded notes in the selected period.'
                : 'No note type data available for this period'}
            </p>
          </div>
        )}
      </div>
    );
  };

  // Render clinician details tab content
  const renderClinicianDetailsTab = () => {
    if (!rsp.data) return null;

    const currentPageDisplayCount = Array.isArray(currentPageClinicians)
      ? currentPageClinicians.length
      : 0;
    const totalCachedClinicians = getAllCachedClinicianData().length;
    const noVisitCount = countNoVisitClinicians();
    const noVisitToggle = noVisitCount > 0 && (
      <label className="no-visit-toggle" data-testid="no-visit-toggle">
        <input
          type="checkbox"
          checked={showNoVisitClinicians}
          onChange={(e) => setShowNoVisitClinicians(e.target.checked)}
        />{' '}
        Show {noVisitCount} clinician{noVisitCount === 1 ? '' : 's'} with no
        visits in this period
      </label>
    );

    if (currentPageDisplayCount === 0) {
      return (
        <div className="text-center text-muted" style={{ padding: '3rem' }}>
          <i
            className="fa fa-users"
            style={{ fontSize: '3rem', opacity: 0.3, marginBottom: '1rem' }}
          ></i>
          <p>
            {selectedLabels.length > 0 || selectedUserEmails.length > 0
              ? 'No clinician analytics are available for the selected clinicians and groups.'
              : 'No clinician analytics are available for this period.'}
          </p>
          {noVisitToggle}
          {renderPagination()}
        </div>
      );
    }

    return (
      <>
        {noVisitToggle}
        {/* Pagination controls */}
        {renderPagination()}

        <div className="table-scroll-container">
          <Table className="analytics-table" striped>
            <thead>
              <tr>
                <th
                  className="wrap-header sortable"
                  onClick={() => handleSort('email')}
                >
                  Email{' '}
                  {sortConfig?.key === 'email'
                    ? sortConfig.direction === 'asc'
                      ? '▲'
                      : '▼'
                    : ''}
                </th>
                <th
                  className="wrap-header sortable"
                  onClick={() => handleSort('visits')}
                  style={{ width: '120px', minWidth: '120px' }}
                >
                  Total Visits{' '}
                  {sortConfig?.key === 'visits'
                    ? sortConfig.direction === 'asc'
                      ? '▲'
                      : '▼'
                    : ''}
                </th>
                <th
                  className="wrap-header sortable"
                  onClick={() => handleSort('timeSaved')}
                  style={{ width: '120px', minWidth: '120px' }}
                >
                  Time Saved (Hrs){' '}
                  {sortConfig?.key === 'timeSaved'
                    ? sortConfig.direction === 'asc'
                      ? '▲'
                      : '▼'
                    : ''}
                </th>
                <th
                  className="wrap-header sortable"
                  onClick={() => handleSort('averageScore')}
                  style={{ width: '180px', minWidth: '180px' }}
                >
                  Average Compliance Score{' '}
                  {sortConfig?.key === 'averageScore'
                    ? sortConfig.direction === 'asc'
                      ? '▲'
                      : '▼'
                    : ''}
                </th>
                <th
                  className="wrap-header sortable"
                  onClick={() => handleSort('fComplianceVisits')}
                  style={{ width: '140px', minWidth: '140px' }}
                >
                  Visits with an F{' '}
                  {sortConfig?.key === 'fComplianceVisits'
                    ? sortConfig.direction === 'asc'
                      ? '▲'
                      : '▼'
                    : ''}
                </th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(currentPageClinicians) &&
                currentPageClinicians.map((clinician, index) => {
                  const isExpanded = expandedRows[clinician.email] || false;
                  return (
                    <React.Fragment key={clinician.email}>
                      <tr className="clinician-row">
                        <td>
                          <button
                            type="button"
                            className="clinician-row-toggle"
                            aria-expanded={isExpanded}
                            onClick={() => {
                              const newExpanded = !isExpanded;
                              setExpandedRows(prev => ({
                                ...prev,
                                [clinician.email]: newExpanded
                              }));
                              if (newExpanded) {
                                setSelectedPerson(clinician);
                                // Initialize tab state for this row if not exists
                                if (!expandedRowTabs[clinician.email]) {
                                  setExpandedRowTabs(prev => ({
                                    ...prev,
                                    [clinician.email]: 'overview'
                                  }));
                                }
                                // Fetch data when expanding
                                fetchVisitTypes(clinician.email);
                                fetchVisitsList(clinician.email);
                              }
                            }}
                            title="Click to expand row"
                          >
                            <i 
                              className={`fa ${isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'}`}
                              style={{ fontSize: '0.75rem', flexShrink: 0 }}
                            ></i>
                            <span 
                              title={clinician.email}
                              style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                minWidth: 0,
                                flex: 1,
                              }}
                            >
                              {clinician.email}
                            </span>
                          </button>
                        </td>
                        <td style={{ width: '120px', minWidth: '120px' }}>{clinician.visits}</td>
                        <td style={{ width: '120px', minWidth: '120px' }}>{clinician.timeSaved.toFixed(2)}</td>
                        <td style={{ width: '180px', minWidth: '180px' }}>
                          {(() => {
                            let numericScore = null;
                            const evaluatedVisits =
                              clinician.evaluatedVisits === null ||
                              clinician.evaluatedVisits === undefined
                                ? null
                                : Number(clinician.evaluatedVisits) || 0;
                            
                            // Check if averageScore exists (including 0 as valid)
                            if (clinician.averageScore !== undefined && clinician.averageScore !== null) {
                              if (typeof clinician.averageScore === 'number') {
                                numericScore = clinician.averageScore;
                              } else {
                                const parsed = parseFloat(clinician.averageScore);
                                if (!isNaN(parsed)) {
                                  numericScore = parsed;
                                }
                              }
                            }
                            
                            // Also check the original field name in case it's there
                            if (numericScore === null && clinician.average_score !== undefined && clinician.average_score !== null) {
                              if (typeof clinician.average_score === 'number') {
                                numericScore = clinician.average_score;
                              } else {
                                const parsed = parseFloat(clinician.average_score);
                                if (!isNaN(parsed)) {
                                  numericScore = parsed;
                                }
                              }
                            }

                            if (evaluatedVisits === 0) {
                              numericScore = null;
                            }
                            
                            const displayScore = numericScore !== null ? Math.round(numericScore) : '--';
                            const evaluationCoverage =
                              evaluatedVisits === null
                                ? 'Coverage unavailable'
                                : gradedOf(evaluatedVisits, clinician.gradeableNotes);
                            const badgeColor =
                              numericScore !== null ? scoreBadgeColor(numericScore) : 'secondary';
                            
                            return (
                              <div
                                style={{
                                  alignItems: 'center',
                                  display: 'flex',
                                  gap: '0.5rem',
                                  justifyContent: 'center',
                                  minWidth: 0,
                                  width: '100%',
                                }}
                              >
                                <Badge
                                  color={badgeColor}
                                  style={{
                                    borderRadius: '6px',
                                    fontSize: '0.8rem',
                                    flexShrink: 0,
                                    lineHeight: 1,
                                    padding: '0.35rem 0.45rem',
                                  }}
                                >
                                  {displayScore}
                                </Badge>
                                <small
                                  title={evaluationCoverage}
                                  style={{
                                    color: '#6c757d',
                                    flex: 1,
                                    fontSize: '0.65rem',
                                    lineHeight: 1.2,
                                    minWidth: 0,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {evaluationCoverage}
                                </small>
                              </div>
                            );
                          })()}
                        </td>
                        <td style={{ width: '140px', minWidth: '140px' }}>{clinician.fComplianceVisits}</td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan="5" style={{ padding: 0, backgroundColor: '#f8f9ff' }}>
                            {renderExpandedRowContent(clinician)}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
            </tbody>
          </Table>
        </div>

        {/* Pagination controls at bottom */}
        {renderPagination()}
      </>
    );
  };

  const countMatchingClinicians = () => {
    const selectedUserEmailSet = new Set(selectedUserEmails);
    return filterUsersByLabels(allUsers.filter((user) => user.isActive)).filter(
      (user) =>
        selectedUserEmailSet.size === 0 ||
        selectedUserEmailSet.has(user.email),
    ).length;
  };

  const renderClinicianFilter = () => {
    const heading = (
      <div className="filter-block-label">
        <h5>Clinicians</h5>
        <p>
          Narrow the report by group, by name, or both. With both, a clinician
          has to match both.
        </p>
      </div>
    );

    if (loadingClinicians) {
      return (
        <section className="filter-block">
          {heading}
          <div className="filter-block-body">
            <p className="filter-empty">
              <i
                className="fa fa-spinner fa-spin"
                style={{ marginRight: '0.5rem' }}
              ></i>
              Loading clinicians and groups...
            </p>
          </div>
        </section>
      );
    }

    const activeUsers = allUsers.filter((user) => user.isActive);
    const matchingClinicians = countMatchingClinicians();
    const availableLabels = getAllUniqueLabels();
    const clinicianOptions = activeUsers
      .map((user) => ({
        value: user.email,
        label: user.name ? `${user.name} — ${user.email}` : user.email,
      }))
      .sort((optionA, optionB) => optionA.label.localeCompare(optionB.label));
    const selectedClinicianOptions = selectedUserEmails.map(
      (email) =>
        clinicianOptions.find((option) => option.value === email) || {
          value: email,
          label: email,
        },
    );
    let clinicianFilterSummary = `All ${matchingClinicians} active clinicians are included.`;
    if (selectedLabels.length > 0 && selectedUserEmails.length > 0) {
      clinicianFilterSummary = `${matchingClinicians} active clinician${
        matchingClinicians === 1 ? '' : 's'
      } match both filters.`;
    } else if (selectedUserEmails.length > 0) {
      clinicianFilterSummary = `${matchingClinicians} selected active clinician${
        matchingClinicians === 1 ? '' : 's'
      } included.`;
    } else if (selectedLabels.length > 0) {
      clinicianFilterSummary = `${matchingClinicians} active clinician${
        matchingClinicians === 1 ? '' : 's'
      } in ${selectedLabels.length} selected group${
        selectedLabels.length === 1 ? '' : 's'
      }.`;
    }

    return (
      <section className="filter-block">
        {heading}
        <div className="filter-block-body">
          <div className="filter-field">
            <div className="filter-field-header">
              <span className="filter-field-label">Groups</span>
              {selectedLabels.length > 0 && (
                <button
                  type="button"
                  className="filter-link"
                  onClick={() => setSelectedLabels([])}
                >
                  Clear {selectedLabels.length} selected
                </button>
              )}
            </div>
            {availableLabels.length > 0 ? (
              <div className="group-categories">
                {Object.entries(groupLabelsByPrefix(availableLabels)).map(
                  ([prefix, labels]) => {
                    const isExpanded = !!expandedLabelGroups[prefix];
                    const selectedCount = labels.filter((label) =>
                      selectedLabels.includes(label),
                    ).length;
                    return (
                      <div
                        key={prefix}
                        className={`group-category${isExpanded ? ' expanded' : ''}`}
                      >
                        <button
                          type="button"
                          className="group-category-toggle"
                          aria-expanded={isExpanded}
                          onClick={() =>
                            setExpandedLabelGroups((current) => ({
                              ...current,
                              [prefix]: !isExpanded,
                            }))
                          }
                        >
                          <i className="fa fa-chevron-right group-category-chevron"></i>
                          <span className="group-category-name">
                            {formatGroupPrefix(prefix)}
                          </span>
                          <span
                            className={`group-category-count${
                              selectedCount > 0 ? ' has-selection' : ''
                            }`}
                          >
                            {selectedCount > 0
                              ? `${selectedCount} of ${labels.length} selected`
                              : `${labels.length} group${labels.length === 1 ? '' : 's'}`}
                          </span>
                        </button>
                        {isExpanded && (
                          <div className="group-options">
                            {labels.map((label) => {
                              const isSelected = selectedLabels.includes(label);
                              // The value filtered on still carries the full
                              // "PREFIX-SUFFIX" label; only the displayed text
                              // drops the prefix, since the category already
                              // shows it.
                              const displayText = label.startsWith(`${prefix}-`)
                                ? label.slice(prefix.length + 1)
                                : label;
                              return (
                                <label
                                  key={label}
                                  className={`group-option${
                                    isSelected ? ' selected' : ''
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() =>
                                      setSelectedLabels((currentLabels) =>
                                        currentLabels.includes(label)
                                          ? currentLabels.filter(
                                              (currentLabel) =>
                                                currentLabel !== label,
                                            )
                                          : [...currentLabels, label],
                                      )
                                    }
                                  />
                                  <span title={label}>{displayText}</span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  },
                )}
              </div>
            ) : (
              <p className="filter-empty">
                No clinician groups have been set up for this account.
              </p>
            )}
          </div>

          <div className="filter-field">
            <Label for="analyticsClinicianFilter" className="filter-field-label">
              Specific clinicians
            </Label>
            <Select
              inputId="analyticsClinicianFilter"
              isMulti
              closeMenuOnSelect={false}
              options={clinicianOptions}
              value={selectedClinicianOptions}
              onChange={(selectedOptions) =>
                setSelectedUserEmails(
                  (selectedOptions || []).map((option) => option.value),
                )
              }
              placeholder="Search by name or email..."
              noOptionsMessage={() => 'No active clinicians found'}
              styles={clinicianSelectStyles}
            />
          </div>

          <p className="filter-hint">
            <i className="fa fa-user-friends"></i>
            {clinicianFilterSummary}
          </p>
        </div>
      </section>
    );
  };

  const closeNoteTypeModal = () => {
    setNoteTypeModalOpen(false);
    setSelectedRuleEvaluation(null);
  };

  return (
    <>
      {/* Outside the !hasData / hasData split on purpose: this panel owns the
          running job's state and poll. Rendered inside the filter form it
          unmounted as soon as results appeared, so the run went invisible and
          nothing refreshed the page when it finished. */}
      <ScoreAllNotesPanel
        ref={scoreAllRef}
        apiClient={apiClient}
        t1={t1}
        t2={t2}
        selectedLabels={selectedLabels}
        selectedUserEmails={selectedUserEmails}
        selectedRuleSet={selectedRuleSet}
        noteSource={noteSource}
        onViewRun={loadScoreAllRunResult}
        onRunComplete={handleScoreAllRunComplete}
        onRunsChanged={() => setScoreAllRunsKey((key) => key + 1)}
      />

      {!hasData && scoreAllRunError && (
        <div className="report-open-error" role="alert" data-testid="report-open-error">
          {scoreAllRunError}
        </div>
      )}

      {!hasData && (
        <div className="analytics-welcome">
          <Form onSubmit={handleFormSubmit} className="analytics-filters">
            <div className="filters-header">
              <p>
                Choose a reporting period and who to include, then generate the
                report.
              </p>
            </div>

            <section className="filter-block">
              <div className="filter-block-label">
                <h5>Reporting period</h5>
                <p>Every metric in the report uses this range.</p>
              </div>
              <div className="filter-block-body">
                <div
                  className="date-presets"
                  role="group"
                  aria-label="Quick date ranges"
                >
                  {DATE_PRESETS.map((preset) => (
                    <button
                      key={preset.id}
                      type="button"
                      className={`date-preset${
                        datePreset === preset.id ? ' active' : ''
                      }`}
                      aria-pressed={datePreset === preset.id}
                      onClick={() => applyDatePreset(preset.id)}
                    >
                      {preset.label}
                    </button>
                  ))}
                  <span
                    className={`date-preset date-preset-custom${
                      datePreset ? '' : ' active'
                    }`}
                  >
                    Custom
                  </span>
                </div>
                <div className="date-inputs">
                  <div className="filter-field">
                    <Label for="startDate" className="filter-field-label">
                      From
                    </Label>
                    <DatePicker
                      id="startDate"
                      selected={t2}
                      onChange={(date) => {
                        setT2(date);
                        setDatePreset(null);
                      }}
                      showTimeSelect
                      timeFormat="HH:mm"
                      timeIntervals={15}
                      timeCaption="time"
                      dateFormat="MMM d, yyyy h:mm aa"
                      className="date-picker-input"
                    />
                  </div>
                  <i
                    className="fa fa-arrow-right date-range-separator"
                    aria-hidden="true"
                  ></i>
                  <div className="filter-field">
                    <Label for="endDate" className="filter-field-label">
                      To
                    </Label>
                    <DatePicker
                      id="endDate"
                      selected={t1}
                      onChange={(date) => {
                        setT1(date);
                        setDatePreset(null);
                      }}
                      showTimeSelect
                      timeFormat="HH:mm"
                      timeIntervals={15}
                      timeCaption="time"
                      dateFormat="MMM d, yyyy h:mm aa"
                      className="date-picker-input"
                    />
                  </div>
                </div>
              </div>
            </section>

            <section className="filter-block">
              <div className="filter-block-label">
                <h5>Scoring</h5>
                <p>Which compliance results count toward the report.</p>
              </div>
              <div className="filter-block-body filter-field-row">
                <div className="filter-field">
                  <Label for="ruleSetFilter" className="filter-field-label">
                    Rule set
                  </Label>
                  <Input
                    id="ruleSetFilter"
                    type="select"
                    className="filter-select"
                    value={selectedRuleSet}
                    onChange={(e) => setSelectedRuleSet(e.target.value)}
                  >
                    {ruleSetOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Input>
                </div>
                {/* Backend only honors `source` for users with the
                    score_all_notes flag; without it the toggle would be inert. */}
                {apiClient?.features?.score_all_notes && (
                  <div className="filter-field">
                    <Label for="noteSourceFilter" className="filter-field-label">
                      Note source
                    </Label>
                    <Input
                      id="noteSourceFilter"
                      type="select"
                      className="filter-select"
                      value={noteSource}
                      onChange={(e) => setNoteSource(e.target.value)}
                    >
                      <option value="all">All notes</option>
                      <option value="comprehend">Comprehend-graded only</option>
                      <option value="non_comprehend">Clinician-authored only</option>
                    </Input>
                  </div>
                )}
              </div>
            </section>

            {renderClinicianFilter()}

            <div className="filters-footer">
              <p className="filters-summary">
                <span>
                  {formatFilterDate(t2)} – {formatFilterDate(t1)}
                </span>
                <span className="filters-summary-dot">·</span>
                <span>
                  {ruleSetOptions.find(
                    (option) => option.value === selectedRuleSet,
                  )?.label || 'All rule sets'}
                </span>
                {!loadingClinicians && (
                  <>
                    <span className="filters-summary-dot">·</span>
                    <span>
                      {countMatchingClinicians()} clinician
                      {countMatchingClinicians() === 1 ? '' : 's'}
                    </span>
                  </>
                )}
              </p>
              <Button
                color="primary"
                type="submit"
                disabled={isLoading || loadingClinicians}
                className="submit-button"
              >
                {isLoading ? (
                  <>
                    <i
                      className="fa fa-spinner fa-spin"
                      style={{ marginRight: '8px' }}
                    ></i>
                    Loading Analytics...
                  </>
                ) : (
                  <>
                    <i
                      className="fa fa-chart-bar"
                      style={{ marginRight: '8px' }}
                    ></i>
                    Generate Analytics
                  </>
                )}
              </Button>
            </div>
          </Form>
          <ScoreAllRunsList
            apiClient={apiClient}
            onViewRun={loadScoreAllRunResult}
            refreshKey={scoreAllRunsKey}
            ruleSetOptions={ruleSetOptions}
            viewingJobId={loadingScoreAllRun ? downloadJobId : null}
          />
        </div>
      )}

      {hasData && (
        <div className="search-summary">
          <div className="search-info">
            <span className="search-detail">
              <span className="search-label">Period</span>
              <span className="search-dates">
                {t2.toLocaleDateString()} – {t1.toLocaleDateString()}
              </span>
            </span>
            {reportMode && (
              <span className="search-detail" data-testid="report-criteria">
                <span className="search-label">Report</span>
                <span className="search-dates">
                  {selectedLabels.length > 0 ? selectedLabels.join(', ') : 'All groups'}
                  {selectedUserEmails.length > 0
                    ? ` · ${selectedUserEmails.length} clinician${selectedUserEmails.length === 1 ? '' : 's'}`
                    : ''}
                  {` · ${ruleSetLabel}`}
                  {noteSource !== 'all' ? ` · ${NOTE_SOURCE_LABELS[noteSource] || noteSource}` : ''}
                  {groupFilterActive ? ` · filtered to ${reportGroupFilter.map(labelValue).join(', ')}` : ''}
                  {reportInfo?.builtAt
                    ? ` · built ${new Date(reportInfo.builtAt).toLocaleString()}`
                    : ''}
                </span>
                {!showGradeable && (
                  <span className="search-dates" data-testid="report-outdated">
                    {' · '}Built with the earlier counting rules, so it has no gradeable
                    count; rebuild it for current numbers.
                  </span>
                )}
              </span>
            )}
          </div>
          <div className="search-actions">
            <Button
              color="outline-primary"
              size="sm"
              onClick={handleNewSearch}
              disabled={isAutoLoading}
            >
              <i className="fa fa-edit" style={{ marginRight: '4px' }}></i>
              Update Search
            </Button>
            {/* Both downloads, whichever tab is open. */}
            <Button
              color="success"
              size="sm"
              data-testid="clinician-csv-download"
              onClick={handleDownloadAnalytics}
            >
              <i className="fa fa-download" style={{ marginRight: '4px' }}></i>
              Download Clinician CSV
            </Button>
            <Button
              color="success"
              size="sm"
              data-testid="note-type-csv-download"
              onClick={handleDownloadNoteTypes}
            >
              <i className="fa fa-download" style={{ marginRight: '4px' }}></i>
              Download Note Type CSV
            </Button>
            {rawComplianceButton}
          </div>
          {reportMode && (reportGroups.length > 0 || reportGroupFilter.length > 0) && (
            <div className="report-group-filter" data-testid="report-group-filter">
              <Select
                isMulti
                inputId="reportGroupFilter"
                placeholder="Filter this report by group (company, region, location...)"
                styles={clinicianSelectStyles}
                options={reportGroups.map((group) => ({
                  label: formatGroupPrefix(group.prefix),
                  options: group.options.map(({ label, count }) => ({
                    value: label,
                    label: `${labelValue(label) || label} (${count})`,
                  })),
                }))}
                value={reportGroupFilter.map((label) => ({
                  value: label,
                  label: `${formatGroupPrefix(labelPrefix(label))}: ${labelValue(label)}`,
                }))}
                onChange={(selected) => {
                  setReportGroupFilter((selected || []).map((option) => option.value));
                  setPagination((current) => (current ? { ...current, current_page: 1 } : current));
                }}
              />
              {noteTypesIgnoreGroupFilter && (
                <small>
                  This report was built before group filtering: Performance by
                  Note Type still shows everyone. Generate Analytics to rebuild it.
                </small>
              )}
            </div>
          )}
        </div>
      )}

      {isLoading && (
        <div className="loading-container">
          <div className="loading-spinner">
            <i className="fa fa-spinner fa-spin fa-3x"></i>
            <p>Loading analytics data...</p>
          </div>
        </div>
      )}

      {rsp.data && !isLoading && (
        <>
          {/* Total Analytics Summary */}
          {renderTotalDataSummary()}

          {/* Navigation Tabs */}
          <Nav tabs className="analytics-tabs" role="tablist">
            <NavItem>
              <NavLink
                tag="button"
                type="button"
                role="tab"
                aria-selected={analyticsActiveTab === 'clinicians'}
                className={analyticsActiveTab === 'clinicians' ? 'active' : ''}
                onClick={() => setAnalyticsActiveTab('clinicians')}
              >
                <i className="fa fa-users" style={{ marginRight: '8px' }}></i>
                Clinician Details
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                tag="button"
                type="button"
                role="tab"
                aria-selected={analyticsActiveTab === 'note-types'}
                className={analyticsActiveTab === 'note-types' ? 'active' : ''}
                onClick={() => setAnalyticsActiveTab('note-types')}
              >
                <i className="fas fa-chart-bar" style={{ marginRight: '8px' }}></i>
                Performance by Note Type
              </NavLink>
            </NavItem>
          </Nav>

          {/* Tab Content */}
          <div className="tab-content">
            {analyticsActiveTab === 'clinicians'
              ? renderClinicianDetailsTab()
              : renderNoteTypeSummary()}
          </div>
        </>
      )}
      <style>
        {`
          .analytics-welcome {
            margin: 0;
            padding: 0;
            width: 100%;
          }

          .report-group-filter {
            flex-basis: 100%;
            margin-top: 0.75rem;
          }

          .report-group-filter small {
            color: #8898aa;
            display: block;
            margin-top: 0.35rem;
          }

          .no-visit-toggle {
            color: #525f7f;
            display: block;
            font-size: 0.8rem;
            margin: 0 0 0.75rem 0;
          }

          .report-open-error {
            background: #fff5f7;
            border-left: 3px solid #f5365c;
            border-radius: 6px;
            color: #a61b3c;
            font-size: 0.84rem;
            margin: 0 0 1rem 0;
            padding: 12px 16px;
          }

          .analytics-filters {
            color: #32325d;
          }

          .filters-header {
            border-bottom: 1px solid #e9ecef;
            padding: 0 0 1.25rem;
          }

          .filters-header p {
            color: #8898aa;
            font-size: 0.84rem;
            margin: 0;
          }

          .filter-block {
            border-bottom: 1px solid #eef0f4;
            display: grid;
            gap: 1rem 2.5rem;
            grid-template-columns: minmax(180px, 240px) minmax(0, 1fr);
            padding: 1.5rem 0;
          }

          .filter-block-label h5 {
            color: #32325d;
            font-size: 0.875rem;
            font-weight: 700;
            margin: 0 0 0.25rem;
          }

          .filter-block-label p {
            color: #8898aa;
            font-size: 0.78rem;
            line-height: 1.45;
            margin: 0;
          }

          .filter-block-body {
            display: flex;
            flex-direction: column;
            gap: 1.125rem;
            max-width: 720px;
            min-width: 0;
          }

          .filter-field {
            display: flex;
            flex-direction: column;
            min-width: 0;
          }

          .filter-field-row {
            display: grid;
            gap: 1rem;
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }

          .filter-field-header {
            align-items: baseline;
            display: flex;
            justify-content: space-between;
            margin-bottom: 0.4rem;
          }

          .filter-field-header .filter-field-label {
            margin-bottom: 0;
          }

          .filter-field-label,
          .analytics-filters .filter-field label.filter-field-label {
            color: #525f7f;
            display: block;
            font-size: 0.75rem;
            font-weight: 600;
            letter-spacing: 0.01em;
            margin-bottom: 0.4rem;
          }

          .filter-link {
            background: none;
            border: 0;
            color: #5e72e4;
            cursor: pointer;
            font-size: 0.75rem;
            font-weight: 600;
            padding: 0;
          }

          .filter-link:hover {
            text-decoration: underline;
          }

          .filter-empty {
            color: #8898aa;
            font-size: 0.8rem;
            margin: 0;
          }

          .filter-hint {
            align-items: center;
            color: #525f7f;
            display: flex;
            font-size: 0.78rem;
            gap: 0.5rem;
            margin: 0;
          }

          .filter-hint i {
            color: #8898aa;
          }

          .date-presets {
            display: flex;
            flex-wrap: wrap;
            gap: 0.5rem;
          }

          .date-preset {
            background: white;
            border: 1px solid #d8dce5;
            border-radius: 999px;
            color: #525f7f;
            cursor: pointer;
            font-size: 0.78rem;
            font-weight: 600;
            line-height: 1;
            padding: 0.5rem 0.875rem;
            transition: border-color 0.15s ease, background-color 0.15s ease,
              color 0.15s ease;
          }

          .date-preset:hover {
            border-color: #aeb9ef;
            color: #32325d;
          }

          .date-preset.active {
            background: #5e72e4;
            border-color: #5e72e4;
            color: white;
          }

          .date-preset-custom {
            cursor: default;
            opacity: 0.55;
          }

          .date-preset-custom:hover {
            border-color: #d8dce5;
            color: #525f7f;
          }

          .date-preset-custom.active {
            background: #eef1ff;
            border-color: #aeb9ef;
            color: #3c4bb8;
            opacity: 1;
          }

          .date-inputs {
            align-items: end;
            display: grid;
            gap: 0.75rem;
            grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
          }

          .date-range-separator {
            color: #adb5bd;
            font-size: 0.8rem;
            padding-bottom: 0.9rem;
          }

          .date-inputs .react-datepicker-wrapper {
            width: 100%;
          }

          .date-picker-input,
          .analytics-filters .filter-select {
            background: white;
            border: 1px solid #d8dce5;
            border-radius: 6px;
            color: #32325d;
            font-size: 0.875rem;
            height: 40px;
            padding: 0 0.75rem;
            transition: border-color 0.15s ease, box-shadow 0.15s ease;
            width: 100%;
          }

          .date-picker-input:hover,
          .analytics-filters .filter-select:hover {
            border-color: #b9c1d0;
          }

          .date-picker-input:focus,
          .analytics-filters .filter-select:focus {
            border-color: #5e72e4;
            box-shadow: 0 0 0 3px rgba(94, 114, 228, 0.14);
            outline: none;
          }

          .group-categories {
            border: 1px solid #e3e6ec;
            border-radius: 8px;
            overflow: hidden;
          }

          .group-category + .group-category {
            border-top: 1px solid #eef0f4;
          }

          .group-category-toggle {
            align-items: center;
            background: white;
            border: 0;
            color: #32325d;
            cursor: pointer;
            display: flex;
            font-size: 0.84rem;
            gap: 0.625rem;
            padding: 0.7rem 0.875rem;
            text-align: left;
            transition: background-color 0.15s ease;
            width: 100%;
          }

          .group-category-toggle:hover {
            background: #f8f9fb;
          }

          .group-category-toggle:focus-visible {
            box-shadow: inset 0 0 0 2px rgba(94, 114, 228, 0.35);
            outline: none;
          }

          .group-category-chevron {
            color: #8898aa;
            font-size: 0.65rem;
            transition: transform 0.15s ease;
            width: 10px;
          }

          .group-category.expanded .group-category-chevron {
            transform: rotate(90deg);
          }

          .group-category-name {
            font-weight: 600;
          }

          .group-category-count {
            color: #8898aa;
            font-size: 0.75rem;
            margin-left: auto;
          }

          .group-category-count.has-selection {
            background: #eef1ff;
            border-radius: 999px;
            color: #3c4bb8;
            font-weight: 600;
            padding: 0.15rem 0.55rem;
          }

          .group-options {
            background: #f8f9fb;
            border-top: 1px solid #eef0f4;
            display: grid;
            gap: 0.5rem;
            grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
            max-height: 240px;
            overflow-y: auto;
            padding: 0.75rem;
          }

          .group-option {
            align-items: center;
            background: white;
            border: 1px solid #e3e6ec;
            border-radius: 6px;
            color: #525f7f;
            cursor: pointer;
            display: flex;
            font-size: 0.8rem;
            gap: 0.5rem;
            margin: 0;
            min-width: 0;
            padding: 0.5rem 0.625rem;
            transition: border-color 0.15s ease, background-color 0.15s ease;
          }

          .group-option:hover {
            border-color: #aeb9ef;
          }

          .group-option.selected {
            background: #f3f5ff;
            border-color: #8d9bea;
            color: #32325d;
            font-weight: 600;
          }

          .group-option input {
            accent-color: #5e72e4;
            flex-shrink: 0;
            height: 14px;
            margin: 0;
            width: 14px;
          }

          .group-option span {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .react-datepicker-popper {
            z-index: 9999 !important;
          }

          .filters-footer {
            align-items: center;
            border-bottom: 1px solid #eef0f4;
            display: flex;
            gap: 1rem;
            justify-content: space-between;
            padding: 1.25rem 0;
          }

          .filter-block.recent-runs {
            border-bottom: 0;
          }

          .filters-summary {
            align-items: center;
            color: #525f7f;
            display: flex;
            flex-wrap: wrap;
            font-size: 0.8rem;
            gap: 0.4rem;
            margin: 0;
          }

          .filters-summary-dot {
            color: #adb5bd;
          }

          .submit-button {
            background: #5e72e4;
            border: 1px solid #5e72e4;
            border-radius: 6px;
            box-shadow: none;
            flex-shrink: 0;
            font-size: 0.84rem;
            font-weight: 600;
            min-width: 190px;
            padding: 0.7rem 1.25rem;
          }

          .submit-button:hover {
            background: #4f63d2;
            border-color: #4f63d2;
          }

          .submit-button:active {
            background: #4659c3;
            border-color: #4659c3;
          }

          .analytics-empty-state {
            align-items: center;
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
            padding: 3.5rem 1rem;
            text-align: center;
          }

          .analytics-empty-state-icon {
            color: #aeb9ef;
            font-size: 2rem;
            margin-bottom: 0.5rem;
          }

          .analytics-empty-state h5 {
            color: #32325d;
            font-size: 1rem;
            font-weight: 700;
            margin: 0;
          }

          .analytics-empty-state p {
            color: #8898aa;
            font-size: 0.84rem;
            margin: 0 0 0.75rem;
            max-width: 420px;
          }

          .analytics-summary-grid {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 1.5rem;
            margin: 1.5rem 0;
          }

          .note-type-summary-card {
            margin: 0.5rem 0 1.5rem;
            border: 1px solid rgba(94, 114, 228, 0.1);
            box-shadow: 0 4px 15px rgba(0, 0, 0, 0.06);
          }

          .note-type-summary-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
            gap: 1rem;
          }

          .note-type-summary-header h4 {
            color: #32325d;
            margin: 0 0 0.25rem;
          }

          .note-type-summary-header p {
            color: #8898aa;
            margin: 0;
            font-size: 0.875rem;
          }

          .note-type-collapse-button {
            align-items: center;
            appearance: none;
            background: transparent;
            border: 0;
            box-shadow: none;
            color: #525f7f;
            display: inline-flex;
            height: 2rem;
            justify-content: center;
            line-height: 1;
            padding: 0;
            width: 2rem;
          }

          .note-type-collapse-button:hover {
            color: #5e72e4;
          }

          .note-type-collapse-button:focus {
            box-shadow: none;
            outline: 2px solid rgba(94, 114, 228, 0.35);
            outline-offset: 2px;
          }

          .note-type-summary-table {
            margin-bottom: 0;
          }

          .note-type-summary-table th {
            color: #525f7f;
            font-size: 0.75rem;
            text-transform: uppercase;
            white-space: nowrap;
          }

          .note-type-sort-button {
            align-items: center;
            background: none;
            border: 0;
            color: inherit;
            display: inline-flex;
            font: inherit;
            gap: 0.4rem;
            padding: 0;
            text-transform: inherit;
          }

          .note-type-sort-button span {
            color: #adb5bd;
            font-size: 0.7rem;
          }

          .note-type-sort-button.active span {
            color: #5e72e4;
          }

          .note-type-sort-button:focus {
            outline: 2px solid rgba(94, 114, 228, 0.35);
            outline-offset: 3px;
          }

          .note-type-summary-table td {
            color: #525f7f;
            vertical-align: middle;
          }

          .note-type-row-toggle {
            align-items: center;
            background: transparent;
            border: 0;
            color: #525f7f;
            display: inline-flex;
            gap: 0.65rem;
            padding: 0;
            text-align: left;
          }

          .note-type-row-toggle i {
            color: #5e72e4;
            font-size: 0.75rem;
            width: 0.75rem;
          }

          .note-type-row-toggle:focus {
            outline: 2px solid rgba(94, 114, 228, 0.35);
            outline-offset: 3px;
          }

          .rule-gap-cell {
            background: #f8f9ff;
            padding: 0.75rem !important;
          }

          .rule-gap-header h6 {
            color: #32325d;
            margin: 0 0 0.5rem;
          }

          .rule-gap-empty {
            color: #8898aa;
            margin: 0 0 1rem;
          }

          .rule-gap-coverage {
            background: #fff9e8;
            border-left: 3px solid #f0ad4e;
            color: #6f5a20;
            font-size: 0.75rem;
            margin: 0 0 0.75rem;
            padding: 0.5rem 0.625rem;
          }

          .rule-gap-table {
            background: white;
            margin: 0;
          }

          .rule-gap-table td:first-child {
            min-width: 320px;
            white-space: normal;
          }

          .stat-card {
            background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
            padding: 1.5rem 2rem;
            border-radius: 12px;
            box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05);
            display: flex;
            flex-direction: column;
            justify-content: center;
            min-height: 120px;
            text-align: center;
            border: 1px solid rgba(94, 114, 228, 0.1);
            transition: all 0.3s ease;
            position: relative;
            overflow: hidden;
          }

          .stat-card::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 4px;
            background: linear-gradient(90deg, #5e72e4 0%, #825ee4 100%);
          }

          .stat-card:hover {
            transform: translateY(-4px);
            box-shadow: 0 8px 25px rgba(94, 114, 228, 0.15), 0 3px 6px rgba(0, 0, 0, 0.1);
          }

          .stat-label {
            color: #6c757d;
            font-size: 0.75rem;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-bottom: 0.5rem;
          }

          .stat-value {
            color: #32325d;
            font-size: 2rem;
            font-weight: 700;
            background: linear-gradient(135deg, #5e72e4 0%, #825ee4 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
          }

          .search-summary {
            align-items: center;
            border-bottom: 1px solid #e9ecef;
            display: flex;
            flex-wrap: wrap;
            gap: 1rem;
            justify-content: space-between;
            margin-bottom: 1rem;
            padding: 0 0 0.75rem;
          }

          .search-info {
            align-items: center;
            display: flex;
            gap: 0.75rem;
            min-width: 0;
          }

          .search-detail {
            align-items: baseline;
            display: flex;
            gap: 0.4rem;
            min-width: 0;
          }

          .search-label {
            color: #8898aa;
            font-size: 0.7rem;
            font-weight: 700;
            letter-spacing: 0.04em;
            text-transform: uppercase;
          }

          .search-dates {
            color: #32325d;
            font-size: 0.8rem;
            font-weight: 600;
          }

          .search-actions {
            display: flex;
            flex-shrink: 0;
            gap: 0.5rem;
          }

          .loading-container {
            display: flex;
            justify-content: center;
            align-items: center;
            padding: 2rem;
            min-height: 150px;
          }

          .loading-spinner {
            text-align: center;
            color: #5e72e4;
          }

          .loading-spinner p {
            margin-top: 0.75rem;
            color: #8898aa;
            font-weight: 500;
          }

          .analytics-tabs {
            margin-bottom: 0.5rem;
            border-bottom: 1px solid #e9ecef;
          }

          .analytics-tabs .nav-link {
            color: #8898aa;
            border: none;
            padding: 0.5rem 1rem;
            font-weight: 500;
            transition: all 0.15s ease-in-out;
          }

          .analytics-tabs .nav-link:hover {
            color: #5e72e4;
            background-color: transparent;
            border-color: transparent;
          }

          .analytics-tabs .nav-link.active {
            color: #5e72e4;
            background-color: transparent;
            border-bottom: 2px solid #5e72e4;
            font-weight: 600;
          }

          .tab-content {
            padding-top: 0.25rem;
          }

          .pagination-container {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin: 0.25rem 0;
            flex-wrap: wrap;
            gap: 0.5rem;
          }

          .analytics-pagination {
            margin: 0;
          }

          .analytics-pagination-list {
            margin: 0;
          }

          .analytics-pagination .page-item {
            margin: 0 0 0 -1px;
          }

          .analytics-pagination .page-item:first-child {
            margin-left: 0;
          }

          .analytics-pagination .page-item .page-link {
            align-items: center;
            background: white;
            border: 1px solid #dfe3ea;
            border-radius: 0 !important;
            box-shadow: none;
            color: #525f7f;
            display: inline-flex;
            font-size: 0.75rem;
            gap: 0.35rem;
            height: 30px;
            justify-content: center;
            margin: 0;
            min-width: 30px;
            padding: 0.3rem 0.55rem;
            width: auto;
          }

          .analytics-pagination .page-item:first-child .page-link {
            border-radius: 6px 0 0 6px !important;
          }

          .analytics-pagination .page-item:last-child .page-link {
            border-radius: 0 6px 6px 0 !important;
          }

          .analytics-pagination .page-item .page-link:hover {
            background: #f3f5f8;
            color: #32325d;
          }

          .analytics-pagination .page-item.active .page-link {
            background: #5e72e4;
            border-color: #5e72e4;
            box-shadow: none;
            color: white;
            position: relative;
            z-index: 1;
          }

          .analytics-pagination .page-item.disabled .page-link {
            background: #f8f9fb;
            color: #adb5bd;
            opacity: 1;
          }

          .pagination-info {
            color: #8898aa;
            font-size: 0.875rem;
            font-weight: 500;
            margin-left: auto;
          }

          /* Tablet and smaller screens */
          @media screen and (max-width: 992px) {
            .analytics-welcome {
              padding: 0;
            }

            .filter-block {
              grid-template-columns: 1fr;
              gap: 0.875rem;
            }

            .analytics-summary-grid {
              grid-template-columns: repeat(4, 1fr);
              gap: 0.5rem;
            }

            .stat-card {
              padding: 0.75rem 1rem;
              min-height: 90px;
            }

            .pagination-container {
              flex-direction: column;
              align-items: center;
            }

            .pagination-info {
              margin-left: 0;
            }

            .search-info {
              align-items: stretch;
              flex-direction: column;
              gap: 0.4rem;
            }

            .search-summary {
              align-items: stretch;
            }

            .search-detail {
              justify-content: space-between;
            }

            .search-actions {
              align-items: center;
            }

            .analytics-tabs .nav-link {
              padding: 0.5rem 1rem;
              font-size: 0.875rem;
            }
          }

          /* Very small screens */
          @media screen and (max-width: 480px) {
            .date-inputs,
            .filter-field-row,
            .group-options {
              grid-template-columns: 1fr;
            }

            .date-range-separator {
              display: none;
            }

            .filters-footer {
              align-items: stretch;
              flex-direction: column;
            }

            .submit-button {
              width: 100%;
            }

            .analytics-summary-grid {
              grid-template-columns: repeat(4, 1fr);
              gap: 0.25rem;
            }

            .stat-card {
              padding: 0.5rem 0.75rem;
              min-height: 80px;
            }

            .stat-label {
              font-size: 0.75rem;
            }

            .stat-value {
              font-size: 1.25rem;
            }

            .pagination-info {
              font-size: 0.75rem;
            }

            .search-dates {
              font-size: 0.75rem;
            }

            .search-summary {
              flex-direction: column;
            }

            .search-actions {
              display: grid;
              grid-template-columns: repeat(2, minmax(0, 1fr));
            }

            .search-actions .btn {
              width: 100%;
            }

            .analytics-tabs .nav-link {
              padding: 0.5rem 0.75rem;
              font-size: 0.8rem;
            }
          }

          /* Keep existing table styles */
          .table-scroll-container {
            width: 100%;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }

          .analytics-table {
            min-width: 1100px;
            margin-bottom: 1rem;
            border-collapse: separate;
            border-spacing: 0;
          }

          .analytics-table thead th {
            background: #f0f2f6;
            color: #525f7f;
            font-size: 0.7rem;
            font-weight: 600;
            line-height: 1.2;
            padding: 0.625rem 0.75rem;
            border: none;
            border-bottom: 1px solid #dfe3ea;
            position: sticky;
            top: 0;
            z-index: 10;
            box-shadow: none;
          }

          .analytics-table thead th:first-child {
            border-top-left-radius: 8px;
          }

          .analytics-table thead th:last-child {
            border-top-right-radius: 8px;
          }

          .analytics-table tbody td {
            padding: 1rem;
            border-bottom: 1px solid #e9ecef;
          }

          .analytics-table .clinician-row > td {
            padding: 0.625rem 0.75rem;
            transition: background-color 0.15s ease;
            vertical-align: middle;
          }

          .analytics-table .clinician-row:hover > td {
            background-color: #f7f8fb;
          }

          .analytics-table tbody tr:last-child td {
            border-bottom: none;
          }

          .clinician-row-toggle {
            align-items: center;
            appearance: none;
            background: transparent;
            border: 0;
            color: #5e72e4;
            cursor: pointer;
            display: inline-flex;
            font: inherit;
            font-weight: 600;
            gap: 0.5rem;
            min-width: 0;
            padding: 0;
            text-align: left;
            width: 100%;
          }

          .clinician-row-toggle:focus {
            outline: 2px solid rgba(94, 114, 228, 0.35);
            outline-offset: 3px;
          }

          .sortable {
            cursor: pointer;
            user-select: none;
            transition: all 0.2s ease;
            position: relative;
          }

          .sortable:hover {
            background-color: #e8ebf1 !important;
          }

          .sortable:active {
            background-color: #e1e5ec !important;
          }

          .person-card-modal .modal-body {
            max-height: 70vh;
            overflow-y: auto;
            padding: 1.5rem;
          }

          .person-card-modal .modal-header {
            background: white;
            color: #32325d;
            border-bottom: 1px solid #e6e9ef;
            padding: 0.75rem 1rem;
          }

          .person-card-modal .modal-header .close {
            color: #525f7f;
            font-size: 1.25rem;
            opacity: 0.65;
            text-shadow: none;
          }

          .person-card-modal .modal-header .close:hover {
            opacity: 1;
          }

          .person-card-modal .modal-title,
          .note-type-modal .modal-title {
            max-width: calc(100% - 2.25rem);
            min-width: 0;
            width: 100%;
          }

          .person-card-tabs {
            background: white;
            margin: 0;
            border-bottom: 2px solid #e9ecef;
          }

          .person-card-tabs .nav-link {
            border: none;
            border-bottom: 3px solid transparent;
            transition: all 0.2s ease;
            position: relative;
          }

          .person-card-tabs .nav-link:hover {
            background-color: #f8f9ff;
            color: #5e72e4;
          }

          .person-card-tabs .nav-link.active {
            background-color: transparent;
            color: #5e72e4;
            border-bottom-color: #5e72e4;
            font-weight: 600;
          }

          .person-card-tabs .nav-link.active::after {
            content: '';
            position: absolute;
            bottom: -2px;
            left: 0;
            right: 0;
            height: 3px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border-radius: 2px 2px 0 0;
          }

          .feedback-display {
            background: linear-gradient(135deg, #f8f9ff 0%, #ffffff 100%);
            border-left: 4px solid #5e72e4;
            padding: 1.5rem;
            margin: 1rem 0;
            border-radius: 8px;
            box-shadow: 0 2px 8px rgba(94, 114, 228, 0.1);
          }

          .feedback-item {
            margin: 0.75rem 0;
            padding: 1rem;
            background: white;
            border-radius: 8px;
            border: 1px solid #e9ecef;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
            transition: all 0.2s ease;
          }

          .feedback-item:hover {
            box-shadow: 0 4px 12px rgba(94, 114, 228, 0.15);
            transform: translateX(4px);
          }

          .feedback-item p {
            margin: 0;
            color: #495057;
            line-height: 1.6;
          }

          .modal-content {
            border: none;
            border-radius: 12px;
            box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
            overflow: hidden;
          }

          .modal-header {
            border-bottom: 1px solid #e9ecef;
            padding: 1.5rem;
          }

          .modal-footer {
            border-top: 1px solid #e9ecef;
            padding: 1rem 1.5rem;
            background: #f8f9fa;
          }

          .note-type-modal .modal-header .close {
            color: #525f7f;
            opacity: 0.65;
            text-shadow: none;
            font-size: 1.25rem;
          }

          .note-type-modal .modal-header .close:hover {
            opacity: 1;
          }

          .note-type-modal tbody tr {
            transition: all 0.2s ease;
          }

          .note-type-modal tbody tr:hover {
            background-color: #f7f8fb;
          }

          .note-type-modal thead th {
            vertical-align: top;
          }

          .note-type-modal .modal-dialog {
            max-width: 1600px !important;
            width: 95% !important;
            margin: 1.75rem auto !important;
            display: flex !important;
            align-items: center !important;
            min-height: calc(100% - 3.5rem) !important;
          }

          .note-type-modal .modal-content {
            border: 1px solid #dfe3ea;
            box-shadow: 0 18px 50px rgba(50, 50, 93, 0.16);
            width: 100% !important;
            max-width: 1600px !important;
          }

          .rule-evaluation-arrow::before {
            display: none !important;
          }
        `}
      </style>

      {/* Person Card Modal (Jake's Eval Modal) - Redesigned with Tabs */}
      <Modal
        isOpen={personCardModalOpen}
        toggle={() => {
          setPersonCardModalOpen(false);
          setPersonCardActiveTab('overview');
          setVisitSearchTerm('');
          setVisitTypeFilter('all');
        }}
        size="xl"
        className="person-card-modal"
      >
        <ModalHeader
          toggle={() => {
            setPersonCardModalOpen(false);
            setPersonCardActiveTab('overview');
            setVisitSearchTerm('');
            setVisitTypeFilter('all');
          }}
          style={{
            background: 'white',
            color: '#32325d',
            borderBottom: '1px solid #e6e9ef',
            padding: '0.75rem 1rem',
          }}
        >
          <div
            style={{
              alignItems: 'baseline',
              display: 'flex',
              gap: '0.5rem',
              minWidth: 0,
              width: '100%',
            }}
          >
            <div
              style={{
                color: '#32325d',
                flexShrink: 0,
                fontSize: '0.875rem',
                fontWeight: '700',
              }}
            >
              Clinician analytics
            </div>
            <div
              style={{
                color: '#8898aa',
                flex: 1,
                fontSize: '0.75rem',
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {selectedPerson?.email}
            </div>
          </div>
        </ModalHeader>
        <ModalBody style={{ padding: 0 }}>
          {selectedPerson && (
            <>
              {/* Summary Cards - Always Visible */}
              <div
                style={{
                  background: 'linear-gradient(135deg, #f8f9ff 0%, #ffffff 100%)',
                  padding: '1.5rem',
                  borderBottom: '1px solid #e9ecef',
                }}
              >
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '1rem',
                  }}
                >
                  <div
                    style={{
                      background: 'white',
                      padding: '1.25rem',
                      borderRadius: '10px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                      borderLeft: '4px solid #5e72e4',
                      transition: 'all 0.3s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(94, 114, 228, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
                    }}
                  >
                    <div
                      style={{
                        fontSize: '0.75rem',
                        color: '#6c757d',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        marginBottom: '0.5rem',
                      }}
                    >
                      <i className="fa fa-comments" style={{ marginRight: '0.5rem' }}></i>
                      Total Visits
                    </div>
                    <div
                      style={{
                        fontSize: '2rem',
                        fontWeight: '700',
                        color: '#32325d',
                        background: 'linear-gradient(135deg, #5e72e4 0%, #825ee4 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                      }}
                    >
                      {selectedPerson.visits}
                    </div>
                  </div>
                  <div
                    style={{
                      background: 'white',
                      padding: '1.25rem',
                      borderRadius: '10px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                      borderLeft: '4px solid #28a745',
                      transition: 'all 0.3s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(40, 167, 69, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
                    }}
                  >
                    <div
                      style={{
                        fontSize: '0.75rem',
                        color: '#6c757d',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        marginBottom: '0.5rem',
                      }}
                    >
                      <i className="fa fa-star" style={{ marginRight: '0.5rem' }}></i>
                      Avg Score
                    </div>
                    <div>
                      <Badge
                        color={
                          selectedPerson.avgComplianceScore === 'A'
                            ? 'success'
                            : selectedPerson.avgComplianceScore === 'B'
                              ? 'warning'
                              : selectedPerson.avgComplianceScore === 'C'
                                ? 'warning'
                                : 'danger'
                        }
                        style={{
                          fontSize: '1.5rem',
                          padding: '0.5rem 1rem',
                          fontWeight: '700',
                        }}
                      >
                        {selectedPerson.avgComplianceScore || '--'}
                      </Badge>
                    </div>
                  </div>
                  <div
                    style={{
                      background: 'white',
                      padding: '1.25rem',
                      borderRadius: '10px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                      borderLeft: '4px solid #ffc107',
                      transition: 'all 0.3s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 193, 7, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
                    }}
                  >
                    <div
                      style={{
                        fontSize: '0.75rem',
                        color: '#6c757d',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        marginBottom: '0.5rem',
                      }}
                    >
                      <i className="fa fa-clock" style={{ marginRight: '0.5rem' }}></i>
                      Time Saved
                    </div>
                    <div
                      style={{
                        fontSize: '2rem',
                        fontWeight: '700',
                        color: '#32325d',
                      }}
                    >
                      {selectedPerson.timeSaved.toFixed(1)}h
                    </div>
                  </div>
                  <div
                    style={{
                      background: 'white',
                      padding: '1.25rem',
                      borderRadius: '10px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                      borderLeft: '4px solid #dc3545',
                      transition: 'all 0.3s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(220, 53, 69, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
                    }}
                  >
                    <div
                      style={{
                        fontSize: '0.75rem',
                        color: '#6c757d',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        marginBottom: '0.5rem',
                      }}
                    >
                      <i className="fa fa-exclamation-circle" style={{ marginRight: '0.5rem' }}></i>
                      Visits with F
                    </div>
                    <div
                      style={{
                        fontSize: '2rem',
                        fontWeight: '700',
                        color: '#dc3545',
                      }}
                    >
                      {selectedPerson.fComplianceVisits || 0}
                    </div>
                  </div>
                </div>
              </div>

              {/* Tabs Navigation */}
              <Nav tabs className="person-card-tabs" style={{ margin: 0, borderBottom: '2px solid #e9ecef' }}>
                <NavItem>
                  <NavLink
                    className={personCardActiveTab === 'overview' ? 'active' : ''}
                    onClick={() => setPersonCardActiveTab('overview')}
                    style={{
                      cursor: 'pointer',
                      padding: '1rem 1.5rem',
                      fontWeight: personCardActiveTab === 'overview' ? '600' : '500',
                      color: personCardActiveTab === 'overview' ? '#5e72e4' : '#6c757d',
                      borderBottom: personCardActiveTab === 'overview' ? '3px solid #5e72e4' : 'none',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <i className="fa fa-chart-pie" style={{ marginRight: '0.5rem' }}></i>
                    Overview
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={personCardActiveTab === 'visit-types' ? 'active' : ''}
                    onClick={() => setPersonCardActiveTab('visit-types')}
                    style={{
                      cursor: 'pointer',
                      padding: '1rem 1.5rem',
                      fontWeight: personCardActiveTab === 'visit-types' ? '600' : '500',
                      color: personCardActiveTab === 'visit-types' ? '#5e72e4' : '#6c757d',
                      borderBottom: personCardActiveTab === 'visit-types' ? '3px solid #5e72e4' : 'none',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <i className="fa fa-file-text" style={{ marginRight: '0.5rem' }}></i>
                    Note Types
                    {visitTypes.length > 0 && (
                      <Badge
                        color="primary"
                        pill
                        style={{
                          marginLeft: '0.5rem',
                          fontSize: '0.75rem',
                          padding: '0.25rem 0.5rem',
                        }}
                      >
                        {visitTypes.length}
                      </Badge>
                    )}
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={personCardActiveTab === 'visits' ? 'active' : ''}
                    onClick={() => setPersonCardActiveTab('visits')}
                    style={{
                      cursor: 'pointer',
                      padding: '1rem 1.5rem',
                      fontWeight: personCardActiveTab === 'visits' ? '600' : '500',
                      color: personCardActiveTab === 'visits' ? '#5e72e4' : '#6c757d',
                      borderBottom: personCardActiveTab === 'visits' ? '3px solid #5e72e4' : 'none',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <i className="fa fa-calendar-check" style={{ marginRight: '0.5rem' }}></i>
                    All Visits
                    {visitsList.length > 0 && (
                      <Badge
                        color="primary"
                        pill
                        style={{
                          marginLeft: '0.5rem',
                          fontSize: '0.75rem',
                          padding: '0.25rem 0.5rem',
                        }}
                      >
                        {visitsList.length}
                      </Badge>
                    )}
                  </NavLink>
                </NavItem>
              </Nav>

              {/* Tab Content */}
              <div style={{ padding: '1.5rem', maxHeight: '60vh', overflowY: 'auto' }}>
                <TabContent activeTab={personCardActiveTab}>
                  {/* Overview Tab */}
                  <TabPane tabId="overview">
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                      {/* Quick Stats */}
                      <div
                        style={{
                          background: 'white',
                          padding: '1.5rem',
                          borderRadius: '12px',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                        }}
                      >
                        <h6
                          style={{
                            color: '#32325d',
                            fontWeight: '600',
                            marginBottom: '1rem',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <i
                            className="fa fa-info-circle"
                            style={{ marginRight: '0.5rem', color: '#5e72e4' }}
                          ></i>
                          Quick Stats
                        </h6>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              padding: '0.75rem',
                              background: '#f8f9fa',
                              borderRadius: '6px',
                            }}
                          >
                            <span style={{ color: '#6c757d' }}>Patients Seen:</span>
                            <strong style={{ color: '#32325d' }}>
                              {selectedPerson.patientsSeen || 0}
                            </strong>
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              padding: '0.75rem',
                              background: '#f8f9fa',
                              borderRadius: '6px',
                            }}
                          >
                            <span style={{ color: '#6c757d' }}>Most Active Day:</span>
                            <strong style={{ color: '#32325d' }}>
                              {selectedPerson.activeDay || 'N/A'}
                            </strong>
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              padding: '0.75rem',
                              background: '#f8f9fa',
                              borderRadius: '6px',
                            }}
                          >
                            <span style={{ color: '#6c757d' }}>Visits on Active Day:</span>
                            <strong style={{ color: '#32325d' }}>
                              {selectedPerson.visitsOnActiveDay || 0}
                            </strong>
                          </div>
                        </div>
                      </div>

                      {/* Performance Summary */}
                      <div
                        style={{
                          background: 'white',
                          padding: '1.5rem',
                          borderRadius: '12px',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                        }}
                      >
                        <h6
                          style={{
                            color: '#32325d',
                            fontWeight: '600',
                            marginBottom: '1rem',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <i
                            className="fa fa-chart-line"
                            style={{ marginRight: '0.5rem', color: '#5e72e4' }}
                          ></i>
                          Performance Summary
                        </h6>
                        <div
                          style={{
                            background: 'linear-gradient(135deg, #f8f9ff 0%, #ffffff 100%)',
                            padding: '1rem',
                            borderRadius: '8px',
                            borderLeft: '4px solid #5e72e4',
                          }}
                        >
                          <p style={{ margin: 0, color: '#495057', lineHeight: '1.6' }}>
                            This clinician has completed{' '}
                            <strong>{selectedPerson.visits}</strong> visits with an average
                            compliance score of{' '}
                            <strong
                              style={{
                                color:
                                  selectedPerson.avgComplianceScore === 'A'
                                    ? '#28a745'
                                    : selectedPerson.avgComplianceScore === 'B'
                                      ? '#ffc107'
                                      : '#dc3545',
                              }}
                            >
                              {selectedPerson.avgComplianceScore || '--'}
                            </strong>
                            , saving approximately{' '}
                            <strong>{selectedPerson.timeSaved.toFixed(1)} hours</strong> of
                            documentation time.
                          </p>
                        </div>
                      </div>
                    </div>
                  </TabPane>

                  {/* Visit Types Tab */}
                  <TabPane tabId="visit-types">
                    {loadingVisitTypes ? (
                      <div className="text-center" style={{ padding: '3rem' }}>
                        <i className="fa fa-spinner fa-spin fa-3x" style={{ color: '#5e72e4' }}></i>
                        <p style={{ marginTop: '1rem', color: '#6c757d' }}>Loading visit types...</p>
                      </div>
                    ) : visitTypes.length > 0 ? (
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                          gap: '1rem',
                        }}
                      >
                        {visitTypes.map((vt, idx) => (
                          <div
                            key={idx}
                            style={{
                              background: 'white',
                              padding: '1.5rem',
                              borderRadius: '12px',
                              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                              border: '1px solid #e9ecef',
                              transition: 'all 0.3s ease',
                              cursor: 'pointer',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'translateY(-4px)';
                              e.currentTarget.style.boxShadow = '0 8px 20px rgba(94, 114, 228, 0.15)';
                              e.currentTarget.style.borderColor = '#5e72e4';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
                              e.currentTarget.style.borderColor = '#e9ecef';
                            }}
                            onClick={() => {
                              setSelectedNoteType(vt.config_name);
                              setNoteTypeModalOpen(true);
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'start',
                                marginBottom: '1rem',
                              }}
                            >
                              <h6
                                style={{
                                  color: '#32325d',
                                  fontWeight: '600',
                                  margin: 0,
                                  flex: 1,
                                }}
                              >
                                {vt.config_name || 'Unknown'}
                              </h6>
                              <Badge
                                color={getNoteTypeGradeColor(vt)}
                                style={{
                                  fontSize: '0.875rem',
                                  padding: '0.5rem 0.75rem',
                                  fontWeight: '700',
                                }}
                              >
                                {getNoteTypeDisplayGrade(vt)}
                              </Badge>
                            </div>
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                marginBottom: '0.75rem',
                                paddingBottom: '0.75rem',
                                borderBottom: '1px solid #e9ecef',
                              }}
                            >
                              <div>
                                <div
                                  style={{
                                    fontSize: '0.75rem',
                                    color: '#6c757d',
                                    marginBottom: '0.25rem',
                                  }}
                                >
                                  Average Score
                                </div>
                                <div
                                  style={{
                                    fontSize: '1.25rem',
                                    fontWeight: '700',
                                    color: '#32325d',
                                  }}
                                >
                                  {getNoteTypeAverageScore(vt)}
                                </div>
                              </div>
                              <div style={{ textAlign: 'center' }}>
                                <div
                                  style={{
                                    fontSize: '0.75rem',
                                    color: '#6c757d',
                                    marginBottom: '0.25rem',
                                  }}
                                >
                                  Evaluated
                                </div>
                                <div
                                  style={{
                                    fontSize: '1.25rem',
                                    fontWeight: '700',
                                    color: '#32325d',
                                  }}
                                >
                                  {Number(vt.visits_with_score) || 0}
                                </div>
                              </div>
                              <div style={{ textAlign: 'right' }}>
                                <div
                                  style={{
                                    fontSize: '0.75rem',
                                    color: '#6c757d',
                                    marginBottom: '0.25rem',
                                  }}
                                >
                                  Gradeable Notes
                                </div>
                                <div
                                  style={{
                                    fontSize: '1.25rem',
                                    fontWeight: '700',
                                    color: '#32325d',
                                  }}
                                >
                                  {vt.gradeable_notes ?? vt.visits_with_score}
                                </div>
                              </div>
                            </div>
                            <Button
                              color="primary"
                              size="sm"
                              block
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedNoteType(vt.config_name);
                                setNoteTypeModalOpen(true);
                              }}
                              style={{
                                borderRadius: '6px',
                                fontWeight: '600',
                                marginTop: '0.5rem',
                              }}
                            >
                              <i className="fa fa-eye" style={{ marginRight: '0.5rem' }}></i>
                              View Details
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center text-muted" style={{ padding: '3rem' }}>
                        <i
                          className="fa fa-file-text"
                          style={{ fontSize: '3rem', opacity: 0.3, marginBottom: '1rem' }}
                        ></i>
                        <p>No visit type data available for this period</p>
                      </div>
                    )}
                  </TabPane>

                  {/* Visits Tab */}
                  <TabPane tabId="visits">
                    {/* Search and Filter Bar */}
                    <div
                      style={{
                        display: 'flex',
                        gap: '1rem',
                        marginBottom: '1.5rem',
                        flexWrap: 'wrap',
                      }}
                    >
                      <Input
                        type="text"
                        placeholder="Search visits..."
                        value={visitSearchTerm}
                        onChange={(e) => setVisitSearchTerm(e.target.value)}
                        style={{
                          flex: 1,
                          minWidth: '200px',
                          borderRadius: '8px',
                          border: '2px solid #e9ecef',
                        }}
                      />
                      <Input
                        type="select"
                        value={visitTypeFilter}
                        onChange={(e) => setVisitTypeFilter(e.target.value)}
                        style={{
                          minWidth: '180px',
                          borderRadius: '8px',
                          border: '2px solid #e9ecef',
                        }}
                      >
                        <option value="all">All Note Types</option>
                        {visitTypes.map((vt, idx) => (
                          <option key={idx} value={vt.config_name}>
                            {vt.config_name}
                          </option>
                        ))}
                      </Input>
                    </div>

                    {loadingVisitsList ? (
                      <div className="text-center" style={{ padding: '3rem' }}>
                        <i className="fa fa-spinner fa-spin fa-3x" style={{ color: '#5e72e4' }}></i>
                        <p style={{ marginTop: '1rem', color: '#6c757d' }}>Loading visits...</p>
                      </div>
                    ) : (() => {
                      // Filter visits
                      let filteredVisits = visitsList;
                      if (visitSearchTerm) {
                        filteredVisits = filteredVisits.filter((v) =>
                          v.name.toLowerCase().includes(visitSearchTerm.toLowerCase()),
                        );
                      }
                      if (visitTypeFilter !== 'all') {
                        filteredVisits = filteredVisits.filter(
                          (v) => v.config_name === visitTypeFilter,
                        );
                      }

                      return filteredVisits.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                          {filteredVisits.map((visit) => (
                            <div
                              key={visit.id}
                              style={{
                                background: 'white',
                                padding: '1.25rem',
                                borderRadius: '10px',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                                border: '1px solid #e9ecef',
                                transition: 'all 0.2s ease',
                                cursor: 'pointer',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateX(4px)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(94, 114, 228, 0.15)';
                                e.currentTarget.style.borderColor = '#5e72e4';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateX(0)';
                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.05)';
                                e.currentTarget.style.borderColor = '#e9ecef';
                              }}
                              onClick={() => {
                                setSelectedNoteType(visit.config_name);
                                setSelectedPerson(visit);
                                setNoteTypeModalOpen(true);
                              }}
                            >
                              <div
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                }}
                              >
                                <div style={{ flex: 1 }}>
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '0.75rem',
                                      marginBottom: '0.5rem',
                                    }}
                                  >
                                    <h6
                                      style={{
                                        color: '#32325d',
                                        fontWeight: '600',
                                        margin: 0,
                                      }}
                                    >
                                      {visit.name}
                                    </h6>
                                    {visit.config_name && (
                                      <Badge color="info" pill style={{ fontSize: '0.75rem' }}>
                                        {visit.config_name}
                                      </Badge>
                                    )}
                                    {visit.authorship && (
                                      <Badge
                                        color={
                                          visit.authorship === 'comprehend'
                                            ? 'primary'
                                            : visit.authorship === 'mixed'
                                              ? 'info'
                                              : 'secondary'
                                        }
                                        pill
                                        style={{ fontSize: '0.7rem' }}
                                      >
                                        {visit.authorship === 'comprehend'
                                          ? authorshipLabels.aiAuthored
                                          : visit.authorship === 'mixed'
                                            ? authorshipLabels.mixed
                                            : authorshipLabels.clinicianAuthored}
                                      </Badge>
                                    )}
                                  </div>
                                  <div
                                    style={{
                                      display: 'flex',
                                      gap: '1rem',
                                      alignItems: 'center',
                                      fontSize: '0.875rem',
                                      color: '#6c757d',
                                    }}
                                  >
                                    <span>
                                      <i className="fa fa-calendar" style={{ marginRight: '0.5rem' }}></i>
                                      {new Date(visit.date).toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric',
                                      })}
                                    </span>
                                    {visit.feedback && visit.feedback.length > 0 && (
                                      <span>
                                        <i className="fa fa-comment" style={{ marginRight: '0.5rem' }}></i>
                                        {visit.feedback.length} feedback item(s)
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '1rem',
                                  }}
                                >
                                  {visit.score !== null && visit.score !== undefined ? (
                                    <Badge
                                      color={scoreBadgeColor(visit.score)}
                                      style={{
                                        fontSize: '1rem',
                                        padding: '0.5rem 1rem',
                                        fontWeight: '700',
                                      }}
                                    >
                                      {visit.score}
                                    </Badge>
                                  ) : (
                                    <span className="text-muted">--</span>
                                  )}
                                  <i
                                    className="fa fa-chevron-right"
                                    style={{ color: '#5e72e4', fontSize: '1.25rem' }}
                                  ></i>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center text-muted" style={{ padding: '3rem' }}>
                          <i
                            className="fa fa-search"
                            style={{ fontSize: '3rem', opacity: 0.3, marginBottom: '1rem' }}
                          ></i>
                          <p>No visits found matching your criteria</p>
                        </div>
                      );
                    })()}
                  </TabPane>
                </TabContent>
              </div>
            </>
          )}
        </ModalBody>
        <ModalFooter
          style={{
            borderTop: '1px solid #e9ecef',
            padding: '1rem 1.5rem',
            background: '#f8f9fa',
          }}
        >
          <Button
            color="secondary"
            onClick={() => {
              setPersonCardModalOpen(false);
              setPersonCardActiveTab('overview');
              setVisitSearchTerm('');
              setVisitTypeFilter('all');
            }}
            style={{
              borderRadius: '8px',
              padding: '0.75rem 1.5rem',
              fontWeight: '600',
            }}
          >
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Note Type Drill-Down Modal */}
      <Modal
        isOpen={noteTypeModalOpen}
        toggle={closeNoteTypeModal}
        size="xl"
        className="note-type-modal"
        contentClassName="note-type-modal-content"
        centered={false}
      >
        <ModalHeader
          toggle={closeNoteTypeModal}
          style={{
            background: 'white',
            borderBottom: '1px solid #e6e9ef',
            padding: '0.625rem 1rem',
          }}
        >
          <div
            style={{
              alignItems: 'baseline',
              display: 'flex',
              gap: '0.5rem',
              minWidth: 0,
              width: '100%',
            }}
          >
            <div
              style={{
                color: '#32325d',
                fontSize: '0.875rem',
                fontWeight: '700',
                maxWidth: '60%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {selectedNoteType || 'All Note Types'}
            </div>
            <div
              style={{
                color: '#8898aa',
                flex: 1,
                fontSize: '0.75rem',
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {selectedPerson?.email || selectedPerson?.name}
            </div>
          </div>
        </ModalHeader>
        <p
          style={{
            borderBottom: '1px solid #e9ecef',
            color: '#6c757d',
            flexShrink: 0,
            fontSize: '0.8rem',
            margin: 0,
            padding: '0.75rem 1rem',
          }}
        >
          Columns combine saved rule results across these visits. A blank cell
          means this visit has no saved result for that rule; it does not mean
          “Not met.” Rules can differ by note type or guideline.{' '}
          <Link to="/User/admin/guidelines#compliance" onClick={closeNoteTypeModal}>Review configured guidelines</Link>.
          Click a result to see its recorded explanation.
        </p>
        <ModalBody style={{ padding: 0, background: 'white' }}>
          {/* Visits Table */}
          <div
            style={{
              background: 'white',
              overflow: 'hidden',
            }}
          >
            {(() => {
              const cacheKey = selectedPerson?.email
                ? getVisitsListCacheKey(selectedPerson.email)
                : null;
              const personVisitsList =
                (cacheKey && personCardCache[cacheKey]?.visitsList) ||
                visitsList;
              const filteredVisits = selectedNoteType
                ? personVisitsList.filter(
                    (visit) => visit.config_name === selectedNoteType,
                  )
                : personVisitsList;
              const meaningfulRuleStatuses = new Set([
                'fully met',
                'partially met',
                'not met',
              ]);
              const hasMeaningfulRuleResult = (evaluation) =>
                Boolean(evaluation.requirement_text?.trim()) &&
                meaningfulRuleStatuses.has(
                  evaluation.status?.trim().toLowerCase(),
                );
              const ruleColumns = [];
              const seenRules = new Set();

              filteredVisits.forEach((visit) => {
                (visit.requirement_evaluations || []).forEach((evaluation) => {
                  const requirementText = evaluation.requirement_text?.trim();
                  if (!hasMeaningfulRuleResult(evaluation)) return;

                  const ruleKey = JSON.stringify([
                    evaluation.category || '',
                    requirementText,
                  ]);
                  if (seenRules.has(ruleKey)) return;

                  seenRules.add(ruleKey);
                  ruleColumns.push({
                    key: ruleKey,
                    category: evaluation.category || '',
                    requirementText,
                  });
                });
              });

              const showRawFeedbackColumn =
                ruleColumns.length === 0 ||
                filteredVisits.some(
                  (visit) =>
                    !(visit.requirement_evaluations || []).some(
                      hasMeaningfulRuleResult,
                    ),
                );
              const incompleteRuleVisitCount = filteredVisits.filter(
                (visit) =>
                  visit.has_complete_rule_results_data === false,
              ).length;
              const columnCount =
                3 + ruleColumns.length + (showRawFeedbackColumn ? 1 : 0);

              return (
                <>
                  {incompleteRuleVisitCount > 0 && (
                    <div
                      style={{
                        background: '#fff9e8',
                        borderBottom: '1px solid #f1dfa2',
                        color: '#6f5a20',
                        fontSize: '0.75rem',
                        padding: '0.625rem 1rem',
                      }}
                    >
                      Structured rule results are unavailable for{' '}
                      {incompleteRuleVisitCount} evaluated{' '}
                      {incompleteRuleVisitCount === 1 ? 'visit' : 'visits'}.
                    </div>
                  )}
                  <Table striped size="sm" responsive style={{ margin: 0 }}>
                  <thead>
                    <tr
                      style={{
                        background: '#f0f2f6',
                      }}
                    >
                      <th
                        scope="col"
                        style={{
                          color: '#525f7f',
                          borderBottom: '1px solid #dfe3ea',
                          padding: '0.75rem 1rem',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          minWidth: '180px',
                        }}
                      >
                        Visit Name
                      </th>
                      <th
                        scope="col"
                        style={{
                          color: '#525f7f',
                          borderBottom: '1px solid #dfe3ea',
                          padding: '0.75rem 1rem',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          minWidth: '120px',
                        }}
                      >
                        Date
                      </th>
                      <th
                        scope="col"
                        style={{
                          color: '#525f7f',
                          borderBottom: '1px solid #dfe3ea',
                          padding: '0.75rem 1rem',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                        }}
                      >
                        Score
                      </th>
                      {ruleColumns.map((rule) => (
                        <th
                          key={rule.key}
                          scope="col"
                          style={{
                            color: '#525f7f',
                            borderBottom: '1px solid #dfe3ea',
                            padding: '0.625rem 0.75rem',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            minWidth: '190px',
                            whiteSpace: 'normal',
                          }}
                        >
                          {rule.category && (
                            <div
                              style={{
                                fontSize: '0.65rem',
                                color: '#8898aa',
                                marginBottom: '0.25rem',
                                textTransform: 'uppercase',
                              }}
                            >
                              {rule.category}
                            </div>
                          )}
                          <div style={{ maxHeight: '6rem', overflowY: 'auto' }}>
                            {rule.requirementText}
                          </div>
                        </th>
                      ))}
                      {showRawFeedbackColumn && (
                        <th
                          scope="col"
                          style={{
                            color: '#525f7f',
                            borderBottom: '1px solid #dfe3ea',
                            padding: '0.75rem 1rem',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            minWidth: '300px',
                          }}
                        >
                          Raw Feedback
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredVisits.length === 0 ? (
                      <tr>
                        <td
                          colSpan={columnCount}
                          className="text-center text-muted"
                          style={{ padding: '3rem' }}
                        >
                          <i
                            className="fa fa-info-circle"
                            style={{
                              fontSize: '2.5rem',
                              color: '#5e72e4',
                              opacity: 0.5,
                              marginBottom: '1rem',
                            }}
                          ></i>
                          <div style={{ fontWeight: '600' }}>
                            {selectedNoteType
                              ? `No visits found for note type: ${selectedNoteType}`
                              : 'No visits found'}
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filteredVisits.map((visit) => {
                        const evaluationsByRule = new Map(
                          (visit.requirement_evaluations || []).map(
                            (evaluation) => [
                              JSON.stringify([
                                evaluation.category || '',
                                evaluation.requirement_text?.trim() || '',
                              ]),
                              evaluation,
                            ],
                          ),
                        );
                        return (
                          <tr key={visit.id}>
                            <td style={{ fontWeight: '600' }}>{visit.name}</td>
                            <td>
                              {new Date(visit.date).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                              })}
                            </td>
                            <td>
                              {visit.score !== null &&
                              visit.score !== undefined ? (
                                <Badge
                                  color={scoreBadgeColor(visit.score)}
                                  style={{
                                    fontSize: '0.875rem',
                                    padding: '0.5rem',
                                  }}
                                >
                                  {visit.score}
                                </Badge>
                              ) : (
                                <span className="text-muted">--</span>
                              )}
                            </td>
                            {ruleColumns.map((rule) => {
                                const evaluation = evaluationsByRule.get(
                                  rule.key,
                                );
                                if (!evaluation) {
                                  return (
                                    <td
                                      key={rule.key}
                                      className="text-muted text-center"
                                    >
                                      {visit.has_complete_rule_results_data ===
                                      false
                                        ? 'Unavailable'
                                        : '--'}
                                    </td>
                                  );
                                }

                                const status =
                                  evaluation.status?.trim().toLowerCase() || '';
                                const statusDisplay =
                                  status === 'fully met'
                                    ? {
                                        color: 'success',
                                        icon: 'fa-check',
                                        label: 'Pass',
                                      }
                                    : status === 'partially met'
                                      ? {
                                          color: 'warning',
                                          icon: 'fa-adjust',
                                          label: 'Partial',
                                        }
                                      : status === 'not met'
                                        ? {
                                            color: 'danger',
                                            icon: 'fa-times',
                                            label: 'Fail',
                                          }
                                        : status === 'not applicable'
                                          ? {
                                              color: 'light',
                                              icon: 'fa-minus',
                                              label: 'N/A',
                                            }
                                        : {
                                            color: 'secondary',
                                            icon: 'fa-minus-circle',
                                            label: 'No result',
                                          };
                                const isSelectedResult =
                                  selectedRuleEvaluation?.visit.id === visit.id &&
                                  selectedRuleEvaluation?.evaluation.category ===
                                    evaluation.category &&
                                  selectedRuleEvaluation?.evaluation
                                    .requirement_text ===
                                    evaluation.requirement_text;

                                return (
                                  <td key={rule.key} className="text-center">
                                    <button
                                      type="button"
                                      onClick={(event) =>
                                        setSelectedRuleEvaluation(
                                          isSelectedResult
                                            ? null
                                            : {
                                                visit,
                                                evaluation,
                                                statusDisplay,
                                                popoverKey: `${visit.id}:${rule.key}`,
                                                target: event.currentTarget,
                                              },
                                        )
                                      }
                                      aria-label={`${visit.name}: ${rule.requirementText} — ${statusDisplay.label}. View details.`}
                                      aria-pressed={isSelectedResult}
                                      aria-expanded={isSelectedResult}
                                      aria-describedby={
                                        isSelectedResult
                                          ? 'rule-evaluation-popover'
                                          : undefined
                                      }
                                      data-rule-evaluation-trigger
                                      style={{
                                        background: isSelectedResult
                                          ? '#eef2ff'
                                          : 'transparent',
                                        border: 0,
                                        borderRadius: '6px',
                                        boxShadow: isSelectedResult
                                          ? '0 0 0 2px #5e72e4'
                                          : 'none',
                                        cursor: 'pointer',
                                        padding: '0.25rem',
                                      }}
                                    >
                                      <Badge
                                        color={statusDisplay.color}
                                        style={{
                                          fontSize: '0.75rem',
                                          padding: '0.45rem 0.6rem',
                                        }}
                                      >
                                        <i
                                          className={`fa ${statusDisplay.icon}`}
                                          style={{ marginRight: '0.35rem' }}
                                        ></i>
                                        {statusDisplay.label}
                                      </Badge>
                                    </button>
                                  </td>
                                );
                              })}
                            {showRawFeedbackColumn && (
                              <td
                                style={{
                                  color: '#6c757d',
                                  fontSize: '0.875rem',
                                  lineHeight: '1.5',
                                  whiteSpace: 'normal',
                                }}
                              >
                                {visit.feedback?.length > 0
                                  ? visit.feedback.join(' ')
                                  : 'No feedback'}
                              </td>
                            )}
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                  </Table>
                </>
              );
            })()}
          </div>

          {selectedRuleEvaluation && (
            <Popover
              key={selectedRuleEvaluation.popoverKey}
              id="rule-evaluation-popover"
              isOpen
              target={selectedRuleEvaluation.target}
              toggle={() => setSelectedRuleEvaluation(null)}
              trigger="manual"
              placement="bottom"
              offset={8}
              boundariesElement="viewport"
              container="body"
              arrowClassName="rule-evaluation-arrow"
              innerRef={ruleEvaluationPopoverRef}
              style={{
                border: '1px solid #dfe3f3',
                boxShadow: '0 6px 18px rgba(50, 50, 93, 0.18)',
                maxWidth: '360px',
              }}
            >
              <div
                style={{
                  alignItems: 'flex-start',
                  borderBottom: '1px solid #e9ecef',
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '0.75rem',
                  padding: '0.75rem 0.9rem',
                }}
              >
                <div>
                  <div
                    style={{
                      color: '#32325d',
                      fontSize: '0.875rem',
                      fontWeight: '700',
                      marginBottom: '0.2rem',
                    }}
                  >
                    {selectedRuleEvaluation.evaluation.requirement_text}
                  </div>
                  <div style={{ color: '#8898aa', fontSize: '0.75rem' }}>
                    {selectedRuleEvaluation.visit.name}
                    {' · '}
                    {new Date(
                      selectedRuleEvaluation.visit.date,
                    ).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedRuleEvaluation(null)}
                  aria-label="Close rule feedback"
                  style={{
                    background: 'transparent',
                    border: 0,
                    color: '#8898aa',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    lineHeight: 1,
                    padding: '0.15rem',
                  }}
                >
                  <i className="fa fa-times"></i>
                </button>
              </div>
              <PopoverBody
                style={{
                  color: '#525f7f',
                  lineHeight: '1.5',
                  padding: '0.75rem 0.9rem 0.9rem',
                }}
              >
                <div
                  style={{
                    alignItems: 'center',
                    display: 'flex',
                    gap: '0.4rem',
                    marginBottom: '0.6rem',
                  }}
                >
                  <Badge
                    color={selectedRuleEvaluation.statusDisplay.color}
                  >
                    {selectedRuleEvaluation.statusDisplay.label}
                  </Badge>
                  {selectedRuleEvaluation.evaluation.category && (
                    <span style={{ color: '#8898aa', fontSize: '0.75rem' }}>
                      {selectedRuleEvaluation.evaluation.category}
                    </span>
                  )}
                  {selectedRuleEvaluation.evaluation.is_required && (
                    <span style={{ color: '#8898aa', fontSize: '0.75rem' }}>
                      · Required
                    </span>
                  )}
                </div>
                <div
                  style={{
                    color: '#32325d',
                    fontSize: '0.875rem',
                    whiteSpace: 'normal',
                  }}
                >
                  {selectedRuleEvaluation.evaluation.suggestion ||
                    (selectedRuleEvaluation.evaluation.status
                      ? 'No additional explanation was recorded for this rule.'
                      : 'No stored evaluation result is available for this rule.')}
                </div>
              </PopoverBody>
            </Popover>
          )}

        </ModalBody>
        <ModalFooter
          style={{
            borderTop: '1px solid #e9ecef',
            padding: '0.75rem 1.25rem',
            background: 'white',
          }}
        >
          <Button
            color="outline-secondary"
            onClick={closeNoteTypeModal}
            style={{
              background: 'white',
              border: '1px solid #d8dce5',
              borderRadius: '6px',
              color: '#525f7f',
              padding: '0.5rem 1rem',
              fontWeight: '600',
            }}
          >
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export const AnalyticsDashboardContent = () => {
  const { apiClient } = useContext(GlobalContext);
  const [totalClinicians, setTotalClinicians] = useState(0);
  const [hasData, setHasData] = useState(false);

  const handleDataLoaded = useCallback((total) => {
    setTotalClinicians(total);
    setHasData(true);
  }, []);

  return (
    <>
      <CardHeader
        className="border-0"
        style={{ padding: '1.25rem 10px 0.25rem' }}
      >
        <h3 className="mb-0">
          {hasData
            ? `Analytics Dashboard for ${totalClinicians} Clinicians`
            : 'Analytics Dashboard'}
        </h3>
      </CardHeader>
      <div style={{ padding: '10px' }}>
        {
          <GetAnalytics
            apiClient={apiClient}
            onDataLoaded={handleDataLoaded}
          />
        }
      </div>
    </>
  );
};

const AnalyticsDashboard = () => {
  return (
    <>
      <Header isLite={true} />
      <Container className="mt--7" fluid>
        <Row>
          <div className="col">
            <Card className="shadow">
              <AnalyticsDashboardContent />
            </Card>
          </div>
        </Row>
      </Container>
      <ToastContainer />
    </>
  );
};

export default AnalyticsDashboard;
